import { Check, ChevronLeft, ChevronRight, X } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import WizardSelect from "./WizardSelect";
import WizardDatePicker from "./WizardDatePicker";
import TickerLookupField from "@/domains/watches/components/TickerLookupField";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { Switch } from "@/shared/ui/switch";
import WatchMarketConditionFields from "@/domains/watches/components/WatchMarketConditionFields";
import WatchMovingAverageFields from "@/domains/watches/components/WatchMovingAverageFields";
import WatchMomentumFields from "@/domains/watches/components/WatchMomentumFields";
import WatchOptionConditionFields from "@/domains/watches/components/WatchOptionConditionFields";
import WatchPositionTriggerFields from "@/domains/watches/components/WatchPositionTriggerFields";
import {
  useWatchAuthoringState,
  type WatchAuthoringGoal,
  type WatchMomentumDraft,
  type WatchMovingAverageDraft,
} from "@/domains/watches/hooks/useWatchAuthoringState";
import { useWatchOptionChain } from "@/domains/watches/hooks/useWatchOptionChain";
import type { ApiFn, TickerSuggestion } from "@/shared/lib/types";
import {
  buildMultiLegCandidate,
  buildMultiLegPosition,
  derivePositionBasisPerShare,
  formatPositionOpenedAt,
  getFourLegStrikeOrderIssue,
  type MultiLegDraft,
  type MultiLegDraftLeg,
  type MultiLegStructure,
  type StrategyEntryBasisType,
  POSITION_STRUCTURES,
  parsePositionOpenedAt,
} from "../../lib/multiLegPosition";
import { mapExitRuleToWatchFields } from "../../lib/exitRuleWatchTriggers";
import { buildMarketSignalPreviewBody } from "../../lib/watchSignalDraft";
import {
  buildWatchOptionEvidenceSpec,
  type WatchOptionEvidenceUiDraft,
} from "../../lib/watchOptionEvidenceDraft";
import { canonicalListingIdentityFromSuggestion } from "../../lib/watchListingIdentity";
import {
  describeWatchRecipeLeaf,
  findAuthorableWatchRecipe,
  type WatchRecipeCatalog,
  type WatchRecipeDefinition,
} from "../../lib/watchRecipeCatalog";
import {
  buildWatchRecipeDraft,
  isMatchingWatchRecipeCreationPreview,
  type WatchRecipeCreationPreview,
} from "../../lib/watchRecipePreview";
import { canonicalRatioToPercent, percentToCanonicalRatio } from "../../lib/watchRatioPresentation";
import type { WatchOptionEvidenceCapability } from "../../lib/watchOptionEvidenceCapabilities";
import { isWatchOptionEvidenceAuthoringAvailable } from "../../lib/watchOptionEvidenceCapabilities";
import { describeOptionCondition } from "../../lib/watchOptionEvidencePresentation";
import {
  findWatchSignal,
  getAuthorableWatchSignals,
  type WatchSignalCatalog,
  type WatchSignalDefinition,
} from "../../lib/watchSignalCatalog";
import {
  describeMomentumCondition,
  describeMovingAverageCondition,
  describeWatchSignalCondition,
  getReviewedSignalPresenter,
  isCompletedDailyVolatilityPresenter,
  isExactOptionContractPresenter,
  isStandardizedOptionChainPresenter,
} from "../../lib/watchSignalReleaseRegistry";
import { isMatchingSignalCreationPreview } from "../../lib/watchSignalPreview";
import {
  buildPositionExitEvaluationSpec,
  countPositionExitTriggerSelections,
  describePositionExitDraft,
  sharedShortStrikeAnyWarning,
  type CanonicalPositionPayoffAvailability,
  type PositionExitDteOperator,
  type PositionExitLogic,
  type PositionExitShortStrikeSide,
} from "../../lib/watchPositionExitDraft";
import { getDteScheduleReachability } from "../../lib/watchMonitoringWindow";
import {
  buildWatchRequestBody,
  capWatchDurationAtExpiry,
  type WatchPayloadState,
} from "../../lib/watchPayloads";
import styles from "./WatchCreateWizard.module.css";

type WizardGoal = WatchAuthoringGoal;
type StructureEntryTargetType = "NET_DEBIT_LTE" | "NET_CREDIT_GTE";
type WizardStep = "goal" | "structure" | "setup" | "trigger" | "schedule" | "review";
type ValidationTarget =
  | "market-signal"
  | "market-reference"
  | "moving-average-comparison"
  | "market-ticker"
  | "market-threshold"
  | "market-operator"
  | "recipe-parameters"
  | "moving-average-type"
  | "moving-average-window"
  | "moving-average-pair"
  | "moving-average-mode"
  | "moving-average-direction"
  | "momentum-indicator"
  | "momentum-mode"
  | "momentum-comparison"
  | "momentum-direction"
  | "momentum-level"
  | "volatility-measurement"
  | "option-condition"
  | "position-ticker"
  | "position-expiry"
  | "position-opened-date"
  | "position-quantity"
  | "position-basis-type"
  | "position-basis-price"
  | "position-strike"
  | "entry-target";

function watchRecipeErrorCode(reason: unknown): string | null {
  if (reason && typeof reason === "object" && "code" in reason
    && typeof reason.code === "string") return reason.code;
  const message = reason instanceof Error ? reason.message : "";
  return message.match(/WATCH_RECIPE_[A-Z_]+/)?.[0] ?? null;
}

function isUnavailableRecipeCode(code: string | null) {
  return code === "WATCH_RECIPE_DEFINITION_UNAVAILABLE"
    || code === "WATCH_RECIPE_DEFINITION_UNKNOWN";
}

const STEPS: Array<{ id: WizardStep; label: string }> = [
  { id: "goal", label: "Goal" },
  { id: "structure", label: "Details" },
  { id: "trigger", label: "Alert" },
  { id: "schedule", label: "Schedule" },
  { id: "review", label: "Review" },
];

const MOVING_AVERAGE_STEPS: Array<{ id: WizardStep; label: string }> = [
  { id: "goal", label: "Goal" },
  { id: "structure", label: "Condition" },
  { id: "setup", label: "Setup" },
  { id: "trigger", label: "Alert" },
  { id: "schedule", label: "Schedule" },
  { id: "review", label: "Review" },
];

export type WatchWizardCapabilities = {
  /** Position exit is the baseline Watches authoring flow. */
  position?: boolean;
  /** Must come from authenticated server authoring metadata. */
  priceTarget?: boolean;
  /** Must come from authenticated server authoring metadata. */
  setupEntry?: boolean;
  /** Server-advertised strategies. Missing lists retain schema-v1 behavior. */
  positionStructures?: MultiLegStructure[];
  entryStructures?: MultiLegStructure[];
  /** Entry price directions are scoped to a strategy, never inferred globally. */
  entryTargetTypes?: Partial<Record<MultiLegStructure, StructureEntryTargetType[]>>;
};

export type WatchCreationPreview = {
  kind?: "WATCH_CONFIGURATION_V1" | "PRICE_TARGET_V1" | "SIGNAL_WATCH" | "RECIPE_WATCH";
  sentence?: string;
  signal?: { family?: string; name?: string };
  subject?: { ticker?: string };
  condition?: {
    operator?: "gte" | "lte";
    operator_phrase?: string;
    threshold?: { value?: string; display?: string };
    direction?: "UP" | "DOWN" | "ABOVE" | "BELOW";
    magnitude_percent?: { value?: string; display?: string };
    reference?: {
      kind?: "PRIOR_REGULAR_CLOSE" | "REGULAR_SESSION_OPEN";
      label?: string;
    };
    kind?: "STATE" | "CROSSING";
    average?: "SMA" | "EMA";
    window?: { value?: "20" | "50" | "200"; display?: string };
    window_pair?: { value?: "20_50" | "20_200" | "50_200"; display?: string };
  };
  configuration?: {
    state?: "VALID" | "INVALID";
    intent?: string;
    target?: {
      value_per_share?: string;
      unit?: "USD_PER_SHARE";
      direction?: "above" | "below";
    };
    reason_code?: string;
  };
  observation?: {
    state?: "AVAILABLE" | "STALE" | "UNAVAILABLE";
    source?: string;
    unit?: "USD_PER_SHARE";
    value_per_share?: string;
    quoted_at_utc?: string;
    freshness?: { state?: "FRESH" | "STALE" | "UNAVAILABLE"; age_seconds?: number; max_age_seconds?: number };
    reason_code?: string;
  };
};

function isAcceptedCreationPreview(
  preview: WatchCreationPreview | null,
  goal: WizardGoal | null,
  submittedPayload: Record<string, unknown>,
) {
  if (!preview) return false;
  if (preview.kind === "RECIPE_WATCH") {
    return goal === "price_target"
      && isMatchingWatchRecipeCreationPreview(preview, submittedPayload);
  }
  if (preview.kind === "SIGNAL_WATCH") {
    return goal === "price_target"
      && isMatchingSignalCreationPreview(preview, submittedPayload);
  }
  if (preview.configuration?.state !== "VALID") return false;
  return goal === "price_target"
    ? preview.kind === "PRICE_TARGET_V1"
    : preview.kind === "WATCH_CONFIGURATION_V1";
}

export type WatchWizardNotificationPrefs = {
  email?: boolean;
  sms?: boolean;
  telegram?: boolean;
  slack?: boolean;
  whatsapp?: boolean;
  push?: boolean;
  inApp?: boolean;
};

export type WatchCopyMode = "duplicate" | "replace";

/** Safe readable data used only by the routed duplicate/replace authoring flow. */
export type WatchWizardInitialState = {
  goal: "position";
  draft: MultiLegDraft;
  /** Identifies a safe authoring source without invoking duplicate/replace semantics. */
  sourceKind?: "DESK_IDEA";
  sourceLabel?: string;
  profitTarget?: string;
  positionExitLogic?: PositionExitLogic;
  lossLimit?: string;
  maxProfitPercent?: string;
  maxLossPercent?: string;
  dteThreshold?: string;
  dteOperator?: PositionExitDteOperator | null;
  shortStrikeBreach?: boolean;
  shortStrikeSide?: PositionExitShortStrikeSide | null;
  /** Legacy single-threshold hydration retained for existing records. */
  pnlThreshold?: string;
  pnlDirection?: "profit" | "loss";
  duration?: number;
  interval?: number;
  alertMode?: "one_and_done" | "keep_watching";
  marketHoursOnly?: boolean;
  copy?: { mode: WatchCopyMode; sourceLabel: string };
  /**
   * JS-1081: optional idea-ledger provenance. Seeds the reducer's OWN
   * `provenance` state at hydration (see `useWatchAuthoringState.ts`'s
   * field doc for the full doctrine) -- the wizard reads the LIVE reducer
   * value at submission time, never this initial one directly, since the
   * reducer detaches it on an identity-changing edit (ticker, a leg's
   * strike/option-type/side, expiration, or the overall structure) while a
   * trigger-only or basis/quantity edit keeps it. This is a POSITIONAL
   * relationship to the held contract, not to any particular trigger
   * configuration -- adversarial-review FIX 4's deliberate, documented
   * ruling. Server-side, `provenance.ledgerId` is independently re-verified
   * against the real outcome ledger before it is trusted (see
   * `domains/watches/idea_provenance.py`) -- this client-side detachment is
   * a UX nicety that keeps an honest label in front of the customer, never
   * the actual security boundary.
   */
  provenance?: { kind: "TRADE_IDEA_LEDGER"; ledgerId: string };
  /**
   * JS-1081 (adversarial-review FIX 1): a RELATIVE exit rule from a Desk
   * idea -- rule_kind + target/stop multiples + dte_floor, deliberately NOT
   * pre-computed absolute dollar thresholds. Absolute `pnl_usd` thresholds
   * (profitTarget/lossLimit) must NEVER be frozen at hydration time: if the
   * customer edits quantity or per-share basis in the wizard after a
   * pre-filled open, a threshold baked in at click-time would silently stop
   * matching "close at N times the ACTUAL entry," which is the entire point
   * of a value-multiple/percent-of-credit exit rule. When this is present,
   * the wizard recomputes profitTarget/lossLimit/maxProfitPercent/
   * dteThreshold LIVE from the draft's CURRENT confirmed basis and quantity
   * (see the `useEffect` keyed on `derivePositionBasisPerShare(draft)`
   * below) every time either changes, using the SAME
   * `mapExitRuleToWatchFields` pure function a Desk idea's OWN one-time
   * fill-price mapping used to call directly. Percent-vocabulary fields
   * (`max_profit_pct`) were already basis-relative before this fix and are
   * unaffected -- only the `pnl_usd`-shaped fields (profitTarget/lossLimit)
   * are the ones this rule recomputes.
   */
  relativeExitRule?: {
    ruleKind: string;
    targetValue: string;
    stopValue: string;
    dteFloor: number;
  };
};

export interface WatchCreateWizardProps {
  /** Kept for backwards-compatible embedded use. Full-page authoring is always visible. */
  open?: boolean;
  /** A dedicated route uses the same deterministic authoring flow without modal behavior. */
  presentation?: "dialog" | "page";
  onClose: () => void;
  onPreview: (payload: Record<string, unknown>) => Promise<WatchCreationPreview>;
  onCreate: (payload: Record<string, unknown>) => void | Promise<void>;
  capabilities?: WatchWizardCapabilities;
  capabilityState?: "loading" | "ready" | "error";
  onRetryCapabilities?: () => void;
  notificationPrefs?: WatchWizardNotificationPrefs;
  initialState?: WatchWizardInitialState;
  /** Authenticated market-data transport injected by the routed page. */
  marketDataApi?: ApiFn;
  /** Pure authenticated server preview; no position payoff is derived in the browser. */
  onPositionPayoffPreview?: (
    payload: Record<string, unknown>,
    signal: AbortSignal,
  ) => Promise<CanonicalPositionPayoffAvailability>;
  /** Shared security lookup used by every Watch goal. */
  searchTickers?: (query: string) => Promise<TickerSuggestion[]>;
  /** Runtime-decoded authenticated signal catalog. */
  signalCatalog?: WatchSignalCatalog | null;
  /** Runtime-decoded immutable recipe catalog. Empty remains invisible. */
  recipeCatalog?: WatchRecipeCatalog | null;
  /** Runtime-decoded option-condition capability; never inferred locally. */
  optionEvidenceCapability?: WatchOptionEvidenceCapability | null;
  /** Invalidates the action-bound confirmation token on every draft edit. */
  onDraftChange?: () => void;
}

function numberInput(value: string) {
  const normalized = value.trim();
  if (!/^\d+(?:\.\d{1,4})?$/.test(normalized)) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function readableStructure(structure: MultiLegStructure) {
  return POSITION_STRUCTURES.find((item) => item.id === structure)?.label ?? "Options position";
}

/** Derive the only executable opening cash-flow direction from the topology. */
function requiredEntryTargetType(draft: MultiLegDraft): StructureEntryTargetType {
  if (draft.structure === "single") return "NET_DEBIT_LTE";
  if (draft.structure === "vertical") {
    const longLeg = draft.legs.find((leg) => leg.side === "LONG");
    const shortLeg = draft.legs.find((leg) => leg.side === "SHORT");
    const longStrike = Number(longLeg?.strike);
    const shortStrike = Number(shortLeg?.strike);
    if (Number.isFinite(longStrike) && Number.isFinite(shortStrike) && longStrike !== shortStrike) {
      const isDebit = longLeg?.optionType === "call"
        ? longStrike < shortStrike
        : longStrike > shortStrike;
      return isDebit ? "NET_DEBIT_LTE" : "NET_CREDIT_GTE";
    }
    return "NET_DEBIT_LTE";
  }
  return "NET_CREDIT_GTE";
}

function customerPricePreviewReason(reason?: string) {
  switch (reason) {
    case "STALE_QUOTE": return "The latest price is too old to use.";
    case "MISSING_QUOTE":
    case "PRICE_UNAVAILABLE": return "A current market price is not available right now.";
    case "MARKET_CLOSED": return "The market is currently closed, so a current price may not be available.";
    default: return "We could not confirm a current market price.";
  }
}

function structureDescription(structure: MultiLegStructure) {
  if (structure === "single") return "1 leg · Call or put";
  if (structure === "vertical") return "2 legs · Call or put spread";
  if (structure === "iron_condor") return "4 legs · Put spread + call spread";
  return "4 legs · Shared center strike";
}

function daysUntilExpiry(expiry: string) {
  const timestamp = Date.parse(`${expiry}T00:00:00Z`);
  if (!Number.isFinite(timestamp)) return null;
  const marketDateParts = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    timeZone: "America/New_York",
  }).formatToParts(new Date());
  const marketDate = Object.fromEntries(
    marketDateParts
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, Number(part.value)]),
  );
  const todayUtc = Date.UTC(marketDate.year, marketDate.month - 1, marketDate.day);
  return Math.max(0, Math.round((timestamp - todayUtc) / 86_400_000));
}

function formatExpirationDate(expiry: string) {
  const timestamp = Date.parse(`${expiry}T00:00:00Z`);
  if (!Number.isFinite(timestamp)) return expiry;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(timestamp));
}

function formatExpiration(expiry: string) {
  const timestamp = Date.parse(`${expiry}T00:00:00Z`);
  const days = daysUntilExpiry(expiry);
  if (!Number.isFinite(timestamp) || days === null) return expiry;
  return `${formatExpirationDate(expiry)} · ${days} ${days === 1 ? "day" : "days"}`;
}

/** Standard monthly listings expire on the third Friday of their month. */
function isMonthlyExpiry(expiry: string) {
  const timestamp = Date.parse(`${expiry}T00:00:00Z`);
  if (!Number.isFinite(timestamp)) return false;
  const date = new Date(timestamp);
  return date.getUTCDay() === 5 && date.getUTCDate() >= 15 && date.getUTCDate() <= 21;
}

/** Preferred default expiration for an entry candidate: roughly 30-45 days out. */
function preferredEntryExpiry(expiries: string[]) {
  const dated = expiries
    .map((expiry) => ({ expiry, days: daysUntilExpiry(expiry) }))
    .filter((item): item is { expiry: string; days: number } => item.days !== null);
  if (dated.length === 0) return null;
  return (
    dated.find((item) => item.days >= 30 && item.days <= 45)
    ?? dated.find((item) => item.days > 45)
    ?? dated[dated.length - 1]
  ).expiry;
}

/** Plain-language distance between a strike and the current underlying price. */
function describeStrikeVsPrice(strike: string, price: number | null) {
  const value = Number(strike);
  if (price === null || !Number.isFinite(value) || !(price > 0)) return "";
  const ratio = (value - price) / price;
  if (Math.abs(ratio) < 0.005) return " · at the money";
  const percent = Math.abs(ratio) * 100;
  const display = percent >= 9.95 ? String(Math.round(percent)) : percent.toFixed(1);
  return ` · ${display}% ${ratio > 0 ? "above" : "below"} price`;
}

/**
 * Anchor a long strike list at the money: keep strikes within 20% of the
 * current price (never fewer than eleven nearest), plus the current selection.
 */
function nearTheMoneyStrikes(strikes: string[], price: number | null, selected: string) {
  if (price === null || !(price > 0) || strikes.length <= 24) return strikes;
  const within = strikes.filter((strike) => {
    const value = Number(strike);
    return Number.isFinite(value) && Math.abs(value - price) / price <= 0.2;
  });
  const kept = within.length >= 11
    ? within
    : [...strikes]
      .sort((a, b) => Math.abs(Number(a) - price) - Math.abs(Number(b) - price))
      .slice(0, 11);
  if (selected && strikes.includes(selected) && !kept.includes(selected)) kept.push(selected);
  return kept.sort((a, b) => Number(a) - Number(b));
}

/** Quantity-aware entry-price mirror for the complete candidate position. */
function entryTargetMoneyMirror(
  perShareText: string,
  unitLabel: "contract" | "spread",
  quantityText: string,
) {
  const perShare = numberInput(perShareText);
  const quantity = Number(quantityText);
  if (perShare === null || perShare < 0 || !Number.isInteger(quantity) || quantity <= 0) return null;
  const perUnit = perShare * 100;
  const pluralUnit = quantity === 1 ? unitLabel : `${unitLabel}s`;
  return `${formatCompactUsd(perShare)} per share = ${formatCompactUsd(perUnit)} per ${unitLabel} × ${quantity} ${pluralUnit} = ${formatCompactUsd(perUnit * quantity)} total, before fees.`;
}

function formatCompactUsd(value: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/** Confirm the cash direction and whole-position amount for one held option. */
function singleOptionOpeningBasisConfirmation(draft: MultiLegDraft, sideChosen: boolean) {
  if (draft.structure !== "single" || !sideChosen) return null;
  const perShare = numberInput(draft.strategyEntryBasisPerShare || "");
  const quantity = Number(draft.legs[0]?.contracts);
  if (perShare === null || perShare <= 0 || !Number.isInteger(quantity) || quantity <= 0) return null;
  const perContract = perShare * 100;
  const action = draft.legs[0]?.side === "SHORT" ? "received" : "paid";
  return `You ${action} ${formatCompactUsd(perContract)} per contract × ${quantity} ${quantity === 1 ? "contract" : "contracts"} = ${formatCompactUsd(perContract * quantity)} total, before fees.`;
}

function selectedPayoffDollarText(
  maximumUsd: string | null | undefined,
  percent: string,
  outcome: "profit" | "loss",
) {
  if (!maximumUsd || !/^\d+(?:\.\d{1,4})?$/.test(percent.trim())) return null;
  const maximum = Number(maximumUsd);
  const selectedPercent = Number(percent);
  if (!(maximum > 0) || selectedPercent < 0 || selectedPercent > 100) return null;
  const money = (value: number) => new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
  return `${percent.trim()}% of the ${money(maximum)} whole-position maximum ${outcome} is ${money(maximum * selectedPercent / 100)}, fees excluded.`;
}

function multiLegOpeningBasisConfirmation(draft: MultiLegDraft) {
  if (draft.structure === "single") return null;
  if (draft.strategyEntryBasisType !== "NET_DEBIT" && draft.strategyEntryBasisType !== "NET_CREDIT") return null;
  const perShare = numberInput(draft.strategyEntryBasisPerShare || "");
  const quantity = Number(draft.legs[0]?.contracts);
  if (perShare === null || perShare <= 0 || !Number.isInteger(quantity) || quantity <= 0) return null;
  const perSpread = perShare * 100;
  const action = draft.strategyEntryBasisType === "NET_CREDIT" ? "received" : "paid";
  return `You ${action} ${formatCompactUsd(perSpread)} per spread × ${quantity} ${quantity === 1 ? "spread" : "spreads"} = ${formatCompactUsd(perSpread * quantity)} total, before fees.`;
}

function formatStrike(strike: string) {
  const value = Number(strike);
  if (!Number.isFinite(value)) return strike;
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 3,
  }).format(value);
}

function legRoleLabel(structure: MultiLegStructure, index: number) {
  if (structure === "single") return "Option";
  if (structure === "vertical") return index === 0 ? "Long leg" : "Short leg";
  if (structure === "iron_condor") {
    return ["Long put wing", "Short put", "Short call", "Long call wing"][index] ?? `Leg ${index + 1}`;
  }
  return ["Long put wing", "Short put at center", "Short call at center", "Long call wing"][index] ?? `Leg ${index + 1}`;
}

function friendlyLegSummary(leg: MultiLegDraftLeg, expiry: string, draft?: MultiLegDraft) {
  const action = leg.side === "LONG" ? "Bought" : "Sold";
  const optionType = leg.optionType === "call" ? "call" : "put";
  const premiumAction = leg.side === "LONG" ? "paid" : "received";
  const quantity = Number(leg.contracts);
  const contracts = Number.isInteger(quantity) && quantity > 0
    ? `${quantity} ${quantity === 1 ? "contract" : "contracts"}`
    : "Quantity not entered";
  const base = `${action} ${contracts} · ${expiry ? formatExpiration(expiry) : "expiration not entered"} · $${leg.strike || "—"} ${optionType}`;
  // A single-leg position entered with a strategy-level basis has no per-leg
  // premium field; the strategy net debit/credit per share IS that leg's
  // premium. Multi-leg strategy-level entries have no known per-leg premium,
  // so the summary omits the clause instead of showing a "$—" placeholder —
  // the strategy-level basis line below already reports the entry.
  let premiumPerShare = leg.entryPremiumPerShare;
  if (!premiumPerShare && draft?.legs.length === 1 && draft.strategyEntryBasisPerShare) {
    const amountPerShare = numberInput(draft.strategyEntryBasisPerShare);
    if (amountPerShare !== null && amountPerShare > 0) {
      premiumPerShare = draft.strategyEntryBasisPerShare;
    }
  }
  if (!premiumPerShare) return base;
  return `${base} · ${premiumAction} $${premiumPerShare} per share`;
}

function positionEntryBasisSummary(draft: MultiLegDraft) {
  if (draft.strategyEntryBasisType && draft.strategyEntryBasisPerShare) {
    const amountPerShare = numberInput(draft.strategyEntryBasisPerShare);
    if (amountPerShare === null || amountPerShare <= 0) return null;
    const quantity = Number(draft.legs[0]?.contracts);
    const total = Number.isInteger(quantity) && quantity > 0 ? amountPerShare * 100 * quantity : null;
    const direction = draft.strategyEntryBasisType === "NET_CREDIT" ? "credit" : "debit";
    return `Opened for a net ${direction} of $${amountPerShare.toFixed(2)} per share${total === null ? "" : ` · $${total.toFixed(2)} total before fees`}`;
  }
  const premiums = draft.legs.map((leg) => numberInput(leg.entryPremiumPerShare));
  if (premiums.some((premium) => premium === null || premium <= 0)) return null;
  const signedCreditPerShare = draft.legs.reduce((total, leg, index) => (
    total + (leg.side === "SHORT" ? premiums[index]! : -premiums[index]!)
  ), 0);
  const amountPerShare = Math.abs(signedCreditPerShare);
  const quantity = Number(draft.legs[0]?.contracts);
  const total = Number.isInteger(quantity) && quantity > 0 ? amountPerShare * 100 * quantity : null;
  const direction = signedCreditPerShare >= 0 ? "credit" : "debit";
  return `Opened for a net ${direction} of $${amountPerShare.toFixed(2)} per share${total === null ? "" : ` · $${total.toFixed(2)} total before fees`}`;
}

function shortOptionRisk(draft: MultiLegDraft) {
  if (draft.structure !== "single" || draft.legs[0]?.side !== "SHORT") return null;
  return draft.legs[0].optionType === "call"
    ? "A short call can have uncapped loss and assignment risk. This Watch monitors estimated P&L; it will not close the position."
    : "A short put can have substantial downside and assignment risk. This Watch monitors estimated P&L; it will not close the position.";
}

function hasDeliveryChannel(prefs?: WatchWizardNotificationPrefs) {
  return Boolean(prefs?.email || prefs?.sms || prefs?.telegram || prefs?.slack || prefs?.whatsapp || prefs?.push || prefs?.inApp);
}

/** Read-only delivery states shown as chips in the Review step's Delivery
 * section, so the customer sees the exact on/off state per channel without
 * leaving the wizard. Editing stays in Alert Preferences. */
const DELIVERY_CHANNEL_CHIPS = [
  { key: "email", label: "Email", read: (prefs?: WatchWizardNotificationPrefs) => Boolean(prefs?.email), legacy: false },
  { key: "sms", label: "SMS", read: (prefs?: WatchWizardNotificationPrefs) => Boolean(prefs?.sms), legacy: false },
  { key: "telegram", label: "Telegram", read: (prefs?: WatchWizardNotificationPrefs) => Boolean(prefs?.telegram), legacy: false },
  { key: "slack", label: "Slack", read: (prefs?: WatchWizardNotificationPrefs) => Boolean(prefs?.slack), legacy: false },
  { key: "whatsapp", label: "WhatsApp", read: (prefs?: WatchWizardNotificationPrefs) => Boolean(prefs?.whatsapp), legacy: false },
  { key: "push", label: "Push", read: (prefs?: WatchWizardNotificationPrefs) => Boolean(prefs?.push), legacy: true },
  { key: "inApp", label: "In-app", read: (prefs?: WatchWizardNotificationPrefs) => Boolean(prefs?.inApp), legacy: true },
] as const;

/** Push and in-app are not server channels today; show them only when a preference source reports them. */
function visibleDeliveryChips(prefs?: WatchWizardNotificationPrefs) {
  return DELIVERY_CHANNEL_CHIPS.filter((chip) => !chip.legacy || prefs?.[chip.key] !== undefined);
}

function describeNotificationPrefs(prefs?: WatchWizardNotificationPrefs) {
  const channels = [
    prefs?.email ? "email" : null,
    prefs?.sms ? "SMS" : null,
    prefs?.telegram ? "Telegram" : null,
    prefs?.slack ? "Slack" : null,
    prefs?.whatsapp ? "WhatsApp" : null,
    prefs?.push ? "push" : null,
    prefs?.inApp ? "in-app" : null,
  ].filter((channel): channel is string => Boolean(channel));
  return channels.length > 0
    ? `Alerts will be sent by ${channels.join(", ")}.`
    : "No alert delivery channel is turned on yet, so this Watch cannot reach you. Turn one on in Alert Preferences.";
}

export default function WatchCreateWizard({
  open = true,
  presentation = "dialog",
  onClose,
  onPreview,
  onCreate,
  capabilities,
  capabilityState = "ready",
  onRetryCapabilities,
  notificationPrefs,
  initialState,
  marketDataApi,
  onPositionPayoffPreview,
  searchTickers,
  signalCatalog = null,
  recipeCatalog = null,
  optionEvidenceCapability = null,
  onDraftChange,
}: WatchCreateWizardProps) {
  /** Router state may be consumed after mount; retain exactly the draft this wizard hydrated. */
  const [hydratedInitialState] = useState(() => initialState);
  const copyMode = hydratedInitialState?.copy?.mode;
  const sourceLabel = hydratedInitialState?.sourceLabel;
  const [step, setStep] = useState<WizardStep>(() => hydratedInitialState ? "structure" : "goal");
  const [form, dispatch] = useWatchAuthoringState(hydratedInitialState);
  const {
    goal,
    draft,
    structureChosen,
    ticker,
    confirmedTicker,
    confirmedListingIdentity,
    marketSignal,
    marketRecipe,
    marketRecipeParameters,
    optionEvidenceDraft,
    movingAverageDraft,
    momentumDraft,
    targetOperator,
    targetThreshold,
    profitTarget,
    positionExitLogic,
    lossLimit,
    maxProfitPercent,
    maxLossPercent,
    dteThreshold,
    dteOperator,
    shortStrikeBreach,
    shortStrikeSide,
    entryTarget,
    duration,
    interval,
    alertMode,
    marketHoursOnly,
    provenance,
  } = form;
  const [copyConfirmed, setCopyConfirmed] = useState(false);
  const [singleSideChosen, setSingleSideChosen] = useState(Boolean(
    hydratedInitialState?.draft.structure === "single",
  ));
  const [error, setError] = useState("");
  const [validationTarget, setValidationTarget] = useState<ValidationTarget | null>(null);
  const [invalidRecipeParameterId, setInvalidRecipeParameterId] = useState<string | null>(null);
  const [percentageReferenceRequested, setPercentageReferenceRequested] = useState(false);
  const [movingAverageComparisonRequested, setMovingAverageComparisonRequested] = useState(false);
  const [momentumIndicatorRequested, setMomentumIndicatorRequested] = useState(false);
  const [volatilityMeasurementRequested, setVolatilityMeasurementRequested] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [optionConditionNotice, setOptionConditionNotice] = useState("");
  const [serverPreview, setServerPreview] = useState<WatchCreationPreview | null>(null);
  const [previewedPayload, setPreviewedPayload] = useState<string | null>(null);
  /** Schedule summary is always visible; controls expand only when requested. */
  const [scheduleTouched, setScheduleTouched] = useState(false);
  const [showAllStrikes, setShowAllStrikes] = useState(false);
  const [openedDateText, setOpenedDateText] = useState(() => (
    hydratedInitialState ? formatPositionOpenedAt(hydratedInitialState.draft.openedAt).slice(0, 10) : ""
  ));
  const stickyTopRef = useRef<HTMLDivElement>(null);
  const [sidePrice, setSidePrice] = useState<{ ticker: string; price: number; changePercent: number | null } | null>(null);
  const [marketQuote, setMarketQuote] = useState<{ ticker: string; price: number; previousClose: number | null } | null>(null);
  /** Distinguishes an inferred price direction from an explicit customer choice. */
  const [directionSource, setDirectionSource] = useState<"inferred" | "user" | null>(null);
  /** Prefills the target-price field once per ticker while it is still untouched. */
  const targetThresholdPrefilledForRef = useRef<string | null>(null);
  const [previewRetryAvailable, setPreviewRetryAvailable] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const headingId = useId();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const stepContentRef = useRef<HTMLDivElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const targetThresholdRef = useRef<HTMLInputElement>(null);
  const targetOperatorRef = useRef<HTMLFieldSetElement>(null);
  const percentageReferenceRef = useRef<HTMLFieldSetElement>(null);
  const movingAverageComparisonRef = useRef<HTMLFieldSetElement>(null);
  const movingAverageTypeRef = useRef<HTMLFieldSetElement>(null);
  const movingAverageWindowRef = useRef<HTMLFieldSetElement>(null);
  const movingAveragePairRef = useRef<HTMLFieldSetElement>(null);
  const movingAverageModeRef = useRef<HTMLFieldSetElement>(null);
  const movingAverageDirectionRef = useRef<HTMLFieldSetElement>(null);
  const momentumIndicatorRef = useRef<HTMLFieldSetElement>(null);
  const volatilityMeasurementRef = useRef<HTMLFieldSetElement>(null);
  const momentumModeRef = useRef<HTMLFieldSetElement>(null);
  const momentumComparisonRef = useRef<HTMLFieldSetElement>(null);
  const momentumDirectionRef = useRef<HTMLFieldSetElement>(null);
  const momentumLevelRef = useRef<HTMLInputElement>(null);
  const recipeParameterRefs = useRef(new Map<string, HTMLInputElement>());
  const recipeAuthorityObservedRef = useRef(false);
  const initialChainRequestedRef = useRef(false);
  const previewRequestRef = useRef(0);
  const payoffPreviewRequestRef = useRef(0);
  const observedRevisionRef = useRef(form.revision);
  const applyTransition = (action: Parameters<typeof dispatch>[0]) => {
    previewRequestRef.current += 1;
    setPreviewing(false);
    onDraftChange?.();
    if (action.type === "option_evidence.set" || action.type === "option_evidence.remove") {
      setOptionConditionNotice("");
    } else if (optionEvidenceDraft && [
      "goal.changed",
      "structure.changed",
      "ticker.changed",
      "ticker.confirmed",
      "expiry.changed",
      "draft.changed",
      "leg.changed",
      "butterfly_center.changed",
    ].includes(action.type)) {
      setOptionConditionNotice("Contract condition removed because the option position changed. Review the Alert step before creating this Watch.");
    }
    dispatch(action);
  };
  const isPage = presentation === "page";
  const positionEnabled = capabilities?.position !== false;
  const priceTargetEnabled = capabilities?.priceTarget === true;
  const setupEntryEnabled = capabilities?.setupEntry === true;
  const isPosition = goal === "position";
  const positionStrikeOrderIssue = getFourLegStrikeOrderIssue(draft);
  const isStructureEntry = goal === "structure_entry";
  const isMultiLeg = isPosition || isStructureEntry;
  const initialUsesStrategyEntryBasis = Boolean(
    hydratedInitialState?.draft.strategyEntryBasisType || hydratedInitialState?.draft.strategyEntryBasisPerShare,
  );
  const usesStrategyEntryBasis = isPosition && (
    !hydratedInitialState || initialUsesStrategyEntryBasis
  );
  const positionPayoffPreviewPayload = useMemo(() => {
    if (!isPosition) return null;
    try {
      return {
        ticker: draft.underlying.trim().toUpperCase(),
        position: buildMultiLegPosition(draft),
      };
    } catch {
      return null;
    }
  }, [draft, isPosition]);
  const positionPayoffFingerprint = positionPayoffPreviewPayload
    ? JSON.stringify(positionPayoffPreviewPayload)
    : "";
  const [positionPayoffPreview, setPositionPayoffPreview] = useState<{
    state: "idle" | "loading" | "ready" | "error";
    fingerprint: string;
    availability?: CanonicalPositionPayoffAvailability;
  }>({ state: "idle", fingerprint: "" });
  const effectivePositionPayoffPreview: typeof positionPayoffPreview = positionPayoffPreview.fingerprint === positionPayoffFingerprint
    ? positionPayoffPreview
    : {
      state: positionPayoffPreviewPayload && onPositionPayoffPreview ? "loading" as const : "idle" as const,
      fingerprint: positionPayoffFingerprint,
    };
  const marketSignalDefinition = marketSignal
    ? findWatchSignal(
      signalCatalog,
      marketSignal.signalId,
      marketSignal.definitionVersion,
    )
    : null;
  const marketSignalPresenter = marketSignalDefinition
    ? getReviewedSignalPresenter(marketSignalDefinition)
    : null;
  const marketRecipeDefinition = marketRecipe
    ? findAuthorableWatchRecipe(
      recipeCatalog,
      signalCatalog,
      marketRecipe.recipeId,
      marketRecipe.definitionVersion,
    )
    : null;
  const recipeAuthoringParameters = marketRecipeDefinition?.authoring_parameters ?? [];
  const recipeConditionSentences = marketRecipeDefinition?.leaves.flatMap((leaf) => {
    const sentence = describeWatchRecipeLeaf(
      marketRecipeDefinition,
      leaf,
      marketRecipeParameters,
    );
    return sentence ? [{ leafId: leaf.leaf_id, sentence }] : [];
  }) ?? [];
  const isPercentageMove = marketSignalPresenter?.kind === "PERCENTAGE_MOVE";
  const isMovingAverage = movingAverageDraft !== null;
  const isMomentum = momentumDraft !== null;
  const isVolatility = isCompletedDailyVolatilityPresenter(marketSignalPresenter);
  const isStandardizedOptionChain = isStandardizedOptionChainPresenter(marketSignalPresenter);
  const optionEvidenceBaseSubject = isPosition
    ? "POSITION_EXIT" as const
    : isStructureEntry ? "STRUCTURE_ENTRY" as const : null;
  const optionConditionDefinitions = optionEvidenceBaseSubject
    && isWatchOptionEvidenceAuthoringAvailable(
      optionEvidenceCapability,
      optionEvidenceBaseSubject,
    )
    ? getAuthorableWatchSignals(signalCatalog, {
      job: isPosition ? "POSITION" : "OPTIONS_ENTRY",
      subjectScope: "OPTION_CONTRACT",
    }).filter((definition) => isExactOptionContractPresenter(
      getReviewedSignalPresenter(definition),
    ))
    : [];
  const optionConditionPresenter = optionEvidenceDraft?.signalId
    ? optionConditionDefinitions
      .map((definition) => getReviewedSignalPresenter(definition))
      .find((presenter) => presenter?.kind === "EXACT_OPTION_CONTRACT"
        && presenter.identity === `${optionEvidenceDraft.signalId}:${optionEvidenceDraft.definitionVersion}`)
    : null;
  const optionConditionReview = optionConditionPresenter?.kind === "EXACT_OPTION_CONTRACT"
    && optionEvidenceDraft?.direction
    && optionEvidenceDraft.threshold
    ? describeOptionCondition(
      optionConditionPresenter.measurement,
      optionConditionPresenter.triggerMode,
      optionEvidenceDraft.direction,
      optionEvidenceDraft.threshold,
      optionEvidenceDraft.targetLegIndex,
    )
    : null;
  const isCompletedDailyRecipe = marketRecipeDefinition?.evidence_time_policy === "SAME_COMPLETED_REGULAR_SESSION";
  const isCompletedDailySignal = isCompletedDailyRecipe
    || isMovingAverage || isMomentum || isVolatility;
  const activeSteps = isMovingAverage || isMomentum ? MOVING_AVERAGE_STEPS : STEPS;
  /** Schedule edits stay inline on Alert so the journey never gains a surprise step. */
  const visibleSteps = activeSteps.filter((item) => item.id !== "schedule");
  const positionTriggerCount = isPosition ? countPositionExitTriggerSelections({
    logic: positionExitLogic,
    profitTarget,
    lossLimit,
    maxProfitPercent,
    maxLossPercent,
    dteThreshold,
    dteOperator,
    shortStrikeBreach,
    shortStrikeSide,
  }) : 0;
  const positionDteReachability = (() => {
    if (!isPosition || !/^\d{1,4}$/.test(dteThreshold.trim()) || !dteOperator) return null;
    const currentDte = daysUntilExpiry(draft.expiry);
    if (currentDte === null) return null;
    return getDteScheduleReachability({
      currentDte,
      threshold: Number(dteThreshold),
      operator: dteOperator,
      duration,
    });
  })();
  const positionDteConflictBlocks = Boolean(
    positionDteReachability
    && !positionDteReachability.reachable
    && (positionExitLogic === "AND" || positionTriggerCount <= 1),
  );
  /** Only the exact-price condition can show and infer against a live quote. */
  const isPlainPriceCondition = goal === "price_target"
    && !marketRecipeDefinition
    && !isPercentageMove
    && !isMovingAverage
    && !isMomentum
    && !isVolatility
    && !isStandardizedOptionChain;
  const entryTargetType = requiredEntryTargetType(draft);
  const {
    loadingExpiration,
    expiryStrikesByType,
    expDates,
    chainLoadedForTicker,
    chainStatus,
    chainError,
    currentPrice,
    getExpiryDate,
    resetChain,
  } = useWatchOptionChain({
    api: marketDataApi,
    ticker: draft.underlying,
    autoSelect: false,
  });

  useEffect(() => {
    if (observedRevisionRef.current === form.revision) return;
    observedRevisionRef.current = form.revision;
    previewRequestRef.current += 1;
    setServerPreview(null);
    setPreviewedPayload(null);
  }, [form.revision]);

  useEffect(() => {
    if (!marketRecipe) {
      recipeAuthorityObservedRef.current = false;
      return;
    }
    if (marketRecipeDefinition) {
      recipeAuthorityObservedRef.current = true;
      return;
    }
    if (!recipeCatalog || !signalCatalog || !recipeAuthorityObservedRef.current) return;

    recipeAuthorityObservedRef.current = false;
    previewRequestRef.current += 1;
    setPreviewing(false);
    setServerPreview(null);
    setPreviewedPayload(null);
    setInvalidRecipeParameterId(null);
    setStep("structure");
    setValidationTarget("market-signal");
    setError("That guided recipe is no longer available. Choose another market condition.");
    dispatch({
      type: "market_recipe.selected",
      recipe: null,
      parameters: {},
      evaluationIntervalMinutes: 15,
    });
    onDraftChange?.();
    onRetryCapabilities?.();
  }, [
    dispatch,
    marketRecipe,
    marketRecipeDefinition,
    onDraftChange,
    onRetryCapabilities,
    recipeCatalog,
    signalCatalog,
  ]);

  useEffect(() => {
    if (form.revision === 0) return;
    const protectDraft = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", protectDraft);
    return () => window.removeEventListener("beforeunload", protectDraft);
  }, [form.revision]);

  useEffect(() => {
    const requestRevision = payoffPreviewRequestRef.current + 1;
    payoffPreviewRequestRef.current = requestRevision;
    if (!open || !positionPayoffPreviewPayload || !onPositionPayoffPreview) {
      let active = true;
      queueMicrotask(() => {
        if (active && payoffPreviewRequestRef.current === requestRevision) {
          setPositionPayoffPreview({ state: "idle", fingerprint: positionPayoffFingerprint });
        }
      });
      return () => { active = false; };
    }

    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      if (controller.signal.aborted || payoffPreviewRequestRef.current !== requestRevision) return;
      setPositionPayoffPreview({ state: "loading", fingerprint: positionPayoffFingerprint });
      onPositionPayoffPreview(positionPayoffPreviewPayload, controller.signal)
        .then((availability) => {
          if (controller.signal.aborted || payoffPreviewRequestRef.current !== requestRevision) return;
          setPositionPayoffPreview({ state: "ready", fingerprint: positionPayoffFingerprint, availability });
        })
        .catch((reason) => {
          if (controller.signal.aborted || payoffPreviewRequestRef.current !== requestRevision) return;
          if (reason instanceof DOMException && reason.name === "AbortError") return;
          setPositionPayoffPreview({ state: "error", fingerprint: positionPayoffFingerprint });
        });
    }, 200);

    return () => {
      window.clearTimeout(timer);
      if (!controller.signal.aborted) {
        controller.abort();
      }
    };
  }, [onPositionPayoffPreview, open, positionPayoffFingerprint, positionPayoffPreviewPayload]);

  useEffect(() => {
    if (!open) return;
    closeButtonRef.current?.focus();
  }, [open, isPage]);

  useEffect(() => {
    if (!open) return;
    // The ticker-first market step claims its own focus (below) so the
    // customer lands directly in the field the screen is built around,
    // instead of on a heading they'd have to tab past.
    if (step === "structure" && !isMultiLeg) return;
    const heading = stepContentRef.current?.querySelector<HTMLElement>("h2, h3");
    if (!heading) return;
    heading.tabIndex = -1;
    heading.focus();
  }, [open, step, isMultiLeg]);

  useEffect(() => {
    if (!error) return;
    const marketControl = validationTarget === "position-ticker"
      ? stepContentRef.current?.querySelector<HTMLInputElement>('input[aria-label="Underlying symbol"]')
      : validationTarget === "position-expiry"
        ? stepContentRef.current?.querySelector<HTMLElement>('[aria-label="Expiration"]')
        : validationTarget === "position-opened-date"
          ? stepContentRef.current?.querySelector<HTMLElement>('[aria-label="Date opened"]')
        : validationTarget === "position-quantity"
          ? stepContentRef.current?.querySelector<HTMLInputElement>('input[aria-label="Number of spreads"], input[aria-label="Number of contracts"]')
          : validationTarget === "position-basis-type"
            ? stepContentRef.current?.querySelector<HTMLInputElement>('input[name="single-side"], input[name="strategy-entry-basis"]')
            : validationTarget === "position-basis-price"
              ? stepContentRef.current?.querySelector<HTMLInputElement>('input[aria-label="Strategy entry basis in USD per share"]')
              : validationTarget === "position-strike"
                ? stepContentRef.current?.querySelector<HTMLElement>('select[aria-label$="strike"][aria-invalid="true"], input[aria-label$="strike"][aria-invalid="true"]')
    : validationTarget === "market-signal"
      ? stepContentRef.current?.querySelector<HTMLButtonElement>(
        '[aria-label="Available market signals"] button[aria-label]',
      )
      : validationTarget === "market-reference"
        ? percentageReferenceRef.current?.querySelector<HTMLInputElement>("input")
        : validationTarget === "moving-average-comparison"
          ? movingAverageComparisonRef.current?.querySelector<HTMLInputElement>("input")
        : validationTarget === "market-ticker"
        ? stepContentRef.current?.querySelector<HTMLInputElement>(
          'input[aria-label="Underlying symbol"]',
        )
        : validationTarget === "market-threshold"
          ? targetThresholdRef.current
          : validationTarget === "market-operator"
            ? targetOperatorRef.current?.querySelector<HTMLInputElement>("input")
            : validationTarget === "moving-average-type"
              ? movingAverageTypeRef.current?.querySelector<HTMLInputElement>("input")
              : validationTarget === "moving-average-window"
                ? movingAverageWindowRef.current?.querySelector<HTMLInputElement>("input")
                : validationTarget === "moving-average-pair"
                  ? movingAveragePairRef.current?.querySelector<HTMLInputElement>("input")
                  : validationTarget === "moving-average-mode"
                    ? movingAverageModeRef.current?.querySelector<HTMLInputElement>("input")
                    : validationTarget === "moving-average-direction"
                      ? movingAverageDirectionRef.current?.querySelector<HTMLInputElement>("input")
                      : validationTarget === "momentum-indicator"
                        ? momentumIndicatorRef.current?.querySelector<HTMLInputElement>("input")
                        : validationTarget === "volatility-measurement"
                          ? volatilityMeasurementRef.current?.querySelector<HTMLInputElement>("input")
                        : validationTarget === "option-condition"
                          ? stepContentRef.current?.querySelector<HTMLElement>(
                            '[data-option-condition-root="true"] input[aria-invalid="true"], [data-option-condition-root="true"] input:not(:checked), [data-option-condition-root="true"] button',
                          )
                        : validationTarget === "recipe-parameters"
                          ? invalidRecipeParameterId
                            ? recipeParameterRefs.current.get(invalidRecipeParameterId) ?? null
                            : null
                        : validationTarget === "momentum-mode"
                          ? momentumModeRef.current?.querySelector<HTMLInputElement>("input")
                          : validationTarget === "momentum-comparison"
                            ? momentumComparisonRef.current?.querySelector<HTMLInputElement>("input")
                            : validationTarget === "momentum-direction"
                              ? momentumDirectionRef.current?.querySelector<HTMLInputElement>("input")
                              : validationTarget === "momentum-level"
                                ? momentumLevelRef.current
            : null;
    if (marketControl) {
      marketControl.focus();
      return;
    }
    errorRef.current?.focus();
  }, [error, invalidRecipeParameterId, validationTarget]);

  useEffect(() => {
    const initialTicker = hydratedInitialState?.draft.underlying?.trim().toUpperCase();
    if (!open || !marketDataApi || !initialTicker || initialChainRequestedRef.current) return;
    initialChainRequestedRef.current = true;
    void getExpiryDate(initialTicker);
  }, [getExpiryDate, hydratedInitialState, marketDataApi, open]);

  // JS-1081 (adversarial-review FIX 1, round 3 correction): when a Desk idea
  // pre-fill carried a RELATIVE exit rule, recompute the absolute
  // profitTarget/lossLimit/maxProfitPercent/dteThreshold fields LIVE from the
  // draft's CURRENT confirmed basis and quantity every time either changes --
  // never once at hydration time. `derivePositionBasisPerShare` returns
  // `null` (no dispatch) until the customer has entered a valid positive
  // basis and quantity, so this effect is a no-op until the draft is
  // actually ready to price.
  //
  // PER-FIELD OWNERSHIP (round 3 fix): a field starts "auto-managed" the
  // instant a relative rule is detected; the MOMENT the customer manually
  // edits that specific field (typing into it directly, which dispatches
  // its own `profit_target.changed`/etc. action), the reducer permanently
  // detaches JUST that field from this recompute -- the other fields keep
  // auto-deriving independently. This effect never dispatches the raw
  // `*.changed` actions itself (that would incorrectly look like a manual
  // edit and detach the field it just wrote); it dispatches
  // `position_exit_fields.auto_derived`, which the reducer filters against
  // `autoManagedTriggerFields` before applying. When the mapping returns
  // `null` (the rule's polarity no longer matches the current signed basis,
  // e.g. a debit/credit flip), every STILL-auto-managed field is CLEARED
  // (never left stale) so the wizard's normal empty-field validation
  // honestly surfaces rather than submitting a number that no longer
  // corresponds to any real computation. `positionExitLogic` is set once by
  // `buildDeskIdeaWatchInitialState` and is not re-derived here (logic is a
  // customer choice, not a fact of the rule).
  const relativeExitRule = hydratedInitialState?.relativeExitRule;
  const derivedBasis = isPosition && relativeExitRule ? derivePositionBasisPerShare(draft) : null;
  const autoManageStartedRef = useRef(false);
  useEffect(() => {
    if (!relativeExitRule || autoManageStartedRef.current) return;
    autoManageStartedRef.current = true;
    // Seed with every field this rule_kind CAN produce (a positive-fill
    // probe determines the shape; the actual sign is re-checked on every
    // real recompute below) so a field the rule never touches (e.g.
    // maxProfitPercent for LONG_OPTION_VALUE_MULTIPLE) never starts
    // auto-managed with nothing to manage.
    const shape = mapExitRuleToWatchFields(
      {
        rule_kind: relativeExitRule.ruleKind,
        target_value: relativeExitRule.targetValue,
        stop_value: relativeExitRule.stopValue,
        dte_floor: relativeExitRule.dteFloor,
      },
      relativeExitRule.ruleKind === "CREDIT_VERTICAL_BUYBACK" ? "-1" : "1",
    );
    if (!shape) return;
    const fields: Array<"profitTarget" | "lossLimit" | "maxProfitPercent" | "dteThreshold"> = [];
    if (shape.profitTarget !== undefined) fields.push("profitTarget");
    if (shape.lossLimit !== undefined) fields.push("lossLimit");
    if (shape.maxProfitPercent !== undefined) fields.push("maxProfitPercent");
    if (shape.dteThreshold !== undefined) fields.push("dteThreshold");
    dispatch({ type: "position_exit_fields.auto_manage_started", fields });
  }, [relativeExitRule, dispatch]);
  useEffect(() => {
    if (!relativeExitRule || !derivedBasis) return;
    const mapped = mapExitRuleToWatchFields(
      {
        rule_kind: relativeExitRule.ruleKind,
        target_value: relativeExitRule.targetValue,
        stop_value: relativeExitRule.stopValue,
        dte_floor: relativeExitRule.dteFloor,
      },
      derivedBasis.signedPerShare,
      derivedBasis.quantity,
    );
    dispatch({ type: "position_exit_fields.auto_derived", mapped });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `relativeExitRule` is a stable object identity from `initialState`, only its own fields (all primitives) matter for the effect's inputs; `derivedBasis` is derived fresh every render from `draft` and is the actual re-run trigger.
  }, [relativeExitRule, derivedBasis?.signedPerShare, derivedBasis?.quantity, dispatch]);

  useEffect(() => {
    if (!open || step !== "trigger" || !marketDataApi || !isPlainPriceCondition) return;
    const symbol = ticker.trim().toUpperCase();
    if (!/^[A-Z][A-Z0-9.-]{0,14}$/.test(symbol) || marketQuote?.ticker === symbol) return;
    let active = true;
    void marketDataApi<{ metrics?: { close?: unknown; previous_close?: unknown } }>(
      "GET",
      `/api/quotes?ticker=${encodeURIComponent(symbol)}`,
      undefined,
      { silent: true },
    ).then((data) => {
      if (!active) return;
      const close = Number(data?.metrics?.close);
      if (!Number.isFinite(close) || close <= 0) return;
      const previousCloseRaw = Number(data?.metrics?.previous_close);
      const previousClose = Number.isFinite(previousCloseRaw) && previousCloseRaw > 0 ? previousCloseRaw : null;
      setMarketQuote({ ticker: symbol, price: close, previousClose });
    }).catch(() => undefined);
    return () => { active = false; };
  }, [isPlainPriceCondition, marketDataApi, marketQuote, open, step, ticker]);

  useEffect(() => {
    const node = stickyTopRef.current;
    const host = node?.parentElement;
    if (!node || !host) return;
    const sync = () => host.style.setProperty("--watch-sticky-top", `${node.offsetHeight}px`);
    sync();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(sync);
    observer.observe(node);
    return () => observer.disconnect();
  }, [isPage, step]);

  /**
   * Prefills the target price with the live quote the first time it becomes
   * available for a given ticker, but only while the field is genuinely
   * untouched. A customer who clears the field after typing keeps it empty.
   */
  useEffect(() => {
    if (!isPlainPriceCondition) return;
    const symbol = ticker.trim().toUpperCase();
    if (!symbol || marketQuote?.ticker !== symbol) return;
    if (form.targetThreshold !== "") return;
    if (targetThresholdPrefilledForRef.current === symbol) return;
    targetThresholdPrefilledForRef.current = symbol;
    // Dispatched directly (not via applyTransition) so this effect can depend
    // on the stable useReducer dispatch instead of the render-scoped
    // applyTransition closure. Mirrors applyTransition's side effects for a
    // plain threshold change: invalidate the pending preview and notify the
    // page that the draft moved.
    previewRequestRef.current += 1;
    setPreviewing(false);
    onDraftChange?.();
    dispatch({ type: "target_threshold.changed", threshold: marketQuote.price.toFixed(2) });
  }, [dispatch, form.targetThreshold, isPlainPriceCondition, marketQuote, onDraftChange, ticker]);

  const currentStepIndex = visibleSteps.findIndex((item) => item.id === step);
  const underlying = isMultiLeg ? draft.underlying : ticker;

  useEffect(() => {
    if (!isPage || !open || step === "goal" || !marketDataApi) return;
    const symbol = underlying.trim().toUpperCase();
    if (!/^[A-Z][A-Z0-9.-]{0,14}$/.test(symbol) || sidePrice?.ticker === symbol) return;
    let active = true;
    const timer = window.setTimeout(() => {
      void marketDataApi<{ metrics?: { close?: unknown; previous_close?: unknown } }>(
        "GET",
        `/api/quotes?ticker=${encodeURIComponent(symbol)}`,
        undefined,
        { silent: true },
      ).then((data) => {
        if (!active) return;
        const price = Number(data?.metrics?.close);
        if (!Number.isFinite(price) || price <= 0) return;
        const previous = Number(data?.metrics?.previous_close);
        setSidePrice({ ticker: symbol, price, changePercent: Number.isFinite(previous) && previous > 0 ? ((price - previous) / previous) * 100 : null });
      }).catch(() => undefined);
    }, 400);
    return () => { active = false; window.clearTimeout(timer); };
  }, [isPage, marketDataApi, open, sidePrice, step, underlying]);

  if (!open) return null;
  const clearError = () => {
    setError("");
    setValidationTarget(null);
    setInvalidRecipeParameterId(null);
    setPreviewRetryAvailable(false);
  };
  const clearServerPreview = () => {
    previewRequestRef.current += 1;
    setServerPreview(null);
    setPreviewedPayload(null);
  };
  const updateDraft = (patch: Partial<MultiLegDraft>) => {
    clearError();
    clearServerPreview();
    applyTransition({ type: "draft.changed", patch });
  };
  const updateLeg = (index: number, patch: Partial<MultiLegDraftLeg>) => {
    clearError();
    clearServerPreview();
    applyTransition({ type: "leg.changed", index, patch });
  };
  const setButterflyCenterStrike = (strike: string) => {
    clearError();
    clearServerPreview();
    applyTransition({ type: "butterfly_center.changed", strike });
  };
  const clearContractSelections = (underlyingValue: string) => {
    clearError();
    clearServerPreview();
    applyTransition({ type: "options_ticker.changed", ticker: underlyingValue });
  };
  const changeOptionsTicker = (value: string) => {
    clearContractSelections(value);
    resetChain();
  };
  const selectOptionsTicker = (suggestion: TickerSuggestion) => {
    const normalized = suggestion.ticker.trim().toUpperCase();
    resetChain();
    applyTransition({ type: "options_ticker.confirmed", ticker: normalized });
    void getExpiryDate(normalized).then((dates) => {
      // Entry candidates start from an opinionated ~30-45 day expiration; the
      // customer can still choose any listed date. Held positions stay
      // explicit because a silently wrong contract fact would be accepted.
      if (!isStructureEntry || !dates || dates.length === 0) return;
      const preferred = preferredEntryExpiry(dates);
      if (preferred) applyTransition({ type: "expiration.selected", expiry: preferred });
    });
  };
  const changePriceTicker = (value: string) => {
    applyTransition({ type: "market_ticker.changed", ticker: value });
    setDirectionSource(null);
    clearError();
    clearServerPreview();
  };
  const selectPriceTicker = (suggestion: TickerSuggestion) => {
    const normalized = suggestion.ticker.trim().toUpperCase();
    applyTransition({
      type: "market_ticker.confirmed",
      ticker: normalized,
      listingIdentity: canonicalListingIdentityFromSuggestion(suggestion),
    });
    setDirectionSource(null);
    clearError();
    clearServerPreview();
  };
  const chooseExpiration = (expiry: string) => {
    clearError();
    clearServerPreview();
    applyTransition({ type: "expiration.selected", expiry });
  };
  const setStructure = (structure: MultiLegStructure) => {
    clearError();
    clearServerPreview();
    if (structure === "single") setSingleSideChosen(false);
    applyTransition({ type: "structure.selected", structure });
  };
  const setOptionType = (optionType: "call" | "put") => {
    clearError();
    clearServerPreview();
    applyTransition({ type: "option_type.changed", optionType });
  };
  const setSinglePositionSide = (side: "LONG" | "SHORT") => {
    clearError();
    clearServerPreview();
    setSingleSideChosen(true);
    applyTransition({ type: "single_side.changed", side });
  };
  const setStrategyEntryBasisType = (basisType: StrategyEntryBasisType) => {
    clearError();
    clearServerPreview();
    applyTransition({ type: "strategy_basis.changed", basisType });
  };
  const setSharedQuantity = (contracts: string) => {
    clearError();
    clearServerPreview();
    applyTransition({ type: "shared_quantity.changed", contracts });
  };
  const selectMarketSignal = (signal: WatchSignalDefinition) => {
    if (marketSignal?.signalId === signal.signal_id
      && marketSignal.definitionVersion === signal.definition_version) return;
    clearError();
    setDirectionSource(null);
    setPercentageReferenceRequested(false);
    setMovingAverageComparisonRequested(false);
    setMomentumIndicatorRequested(false);
    setVolatilityMeasurementRequested(false);
    resetChain();
    applyTransition({
      type: "market_signal.selected",
      signal: {
        signalId: signal.signal_id,
        definitionVersion: signal.definition_version,
      },
    });
  };
  const selectMarketRecipe = (recipe: WatchRecipeDefinition | null) => {
    if (recipe
      && marketRecipe?.recipeId === recipe.recipe_id
      && marketRecipe.definitionVersion === recipe.definition_version) return;
    clearError();
    setPercentageReferenceRequested(false);
    setMovingAverageComparisonRequested(false);
    setMomentumIndicatorRequested(false);
    setVolatilityMeasurementRequested(false);
    resetChain();
    applyTransition({
      type: "market_recipe.selected",
      recipe: recipe ? {
        recipeId: recipe.recipe_id,
        definitionVersion: recipe.definition_version,
      } : null,
      parameters: recipe ? Object.fromEntries((recipe.authoring_parameters ?? []).map((parameter) => (
        [parameter.parameter_id, parameter.default]
      ))) : {},
      evaluationIntervalMinutes: recipe?.evidence_time_policy === "SAME_COMPLETED_REGULAR_SESSION"
        ? 1440
        : 15,
    });
  };
  const selectMovingAverageComparison = (
    comparison: WatchMovingAverageDraft["comparison"],
  ) => {
    if (movingAverageDraft?.comparison === comparison) return;
    clearError();
    setMovingAverageComparisonRequested(false);
    setPercentageReferenceRequested(false);
    setMomentumIndicatorRequested(false);
    setVolatilityMeasurementRequested(false);
    resetChain();
    applyTransition({ type: "moving_average.comparison_selected", comparison });
  };
  const selectMomentumIndicator = (indicator: WatchMomentumDraft["indicator"]) => {
    if (momentumDraft?.indicator === indicator) return;
    clearError();
    setMomentumIndicatorRequested(false);
    setVolatilityMeasurementRequested(false);
    setMovingAverageComparisonRequested(false);
    setPercentageReferenceRequested(false);
    resetChain();
    applyTransition({ type: "momentum.indicator_selected", indicator });
  };
  const selectGoal = (nextGoal: WizardGoal) => {
    // A duplicate/replacement is deliberately constrained to its held
    // position subject. Changing that subject creates a different Watch.
    if (hydratedInitialState) return;
    if (nextGoal === goal) return;
    setPercentageReferenceRequested(false);
    setMovingAverageComparisonRequested(false);
    setMomentumIndicatorRequested(false);
    setVolatilityMeasurementRequested(false);
    setDirectionSource(null);
    setOpenedDateText("");
    clearError();
    clearServerPreview();
    applyTransition({ type: "goal.selected", goal: nextGoal });
    resetChain();
  };
  /**
   * A goal tap fully answers this screen's one question, so it advances
   * immediately. The step-change effect moves focus to the next heading and
   * the step is announced there; Back and the footer Continue still work.
   */
  const chooseGoalAndAdvance = (nextGoal: WizardGoal) => {
    selectGoal(nextGoal);
    if (!hydratedInitialState) setStep("structure");
  };

  const buildOptionConditionSpec = () => {
    if (!optionEvidenceDraft) return undefined;
    if (!optionEvidenceBaseSubject) throw new Error("This Watch cannot use a contract condition.");
    if (!optionEvidenceDraft.signalId || optionEvidenceDraft.definitionVersion === undefined) {
      throw new Error("Choose what the contract condition should measure.");
    }
    if (!optionEvidenceDraft.direction) {
      throw new Error("Choose whether the contract measurement should be at or above, or at or below, your level.");
    }
    if (!optionEvidenceDraft.threshold) {
      throw new Error("Enter the contract condition level.");
    }
    const completeDraft: WatchOptionEvidenceUiDraft = {
      signalId: optionEvidenceDraft.signalId,
      definitionVersion: optionEvidenceDraft.definitionVersion,
      direction: optionEvidenceDraft.direction,
      threshold: optionEvidenceDraft.threshold,
      ...(optionEvidenceDraft.targetLegIndex === undefined
        ? {}
        : { targetLegIndex: optionEvidenceDraft.targetLegIndex }),
    };
    return buildWatchOptionEvidenceSpec({
      capability: optionEvidenceCapability,
      signalCatalog,
      baseSubject: optionEvidenceBaseSubject,
      position: draft,
      draft: completeDraft,
    });
  };

  const invalidRecipeParameter = () => {
    for (const parameter of recipeAuthoringParameters) {
      const value = marketRecipeParameters[parameter.parameter_id] ?? "";
      const parsed = Number(value);
      const decimalPlaces = value.includes(".") ? value.length - value.indexOf(".") - 1 : 0;
      if (!/^(?:0|[1-9]\d*)(?:\.\d+)?$/.test(value)
        || !Number.isFinite(parsed)
        || parsed < Number(parameter.minimum)
        || parsed > Number(parameter.maximum)
        || decimalPlaces > parameter.max_decimal_places) {
        return {
          parameterId: parameter.parameter_id,
          message: `${parameter.customer.label} must be between ${canonicalRatioToPercent(parameter.minimum)}% and ${canonicalRatioToPercent(parameter.maximum)}%.`,
        };
      }
    }
    return null;
  };

  const structureDraftValidation = () => {
    if (!isMultiLeg) return null;
    const tickerValue = draft.underlying.trim().toUpperCase();
    if (!/^[A-Z]{1,10}$/.test(tickerValue)
      || searchTickers && confirmedTicker !== tickerValue) {
      return { target: "position-ticker" as const, message: searchTickers
        ? "Ticker is missing. Choose a ticker from the search results."
        : "Ticker is missing. Enter a valid underlying ticker." };
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(draft.expiry)) {
      return { target: "position-expiry" as const, message: "Expiry is missing. Choose the option expiration." };
    }
    if (isPosition && openedDateText) {
      try {
        parsePositionOpenedAt(`${openedDateText}T00:00`);
      } catch (reason) {
        return { target: "position-opened-date" as const, message: reason instanceof Error
          ? reason.message : "Enter a valid open date." };
      }
    }
    if (isPosition && draft.structure === "single" && !singleSideChosen) {
      return { target: "position-basis-type" as const, message: "Bought or sold is missing. Choose whether you bought or sold the option." };
    }
    if (draft.legs.some((leg) => numberInput(leg.strike) === null || Number(leg.strike) <= 0)) {
      return { target: "position-strike" as const, message: "Strike is missing. Enter a valid strike for each leg." };
    }
    if (positionStrikeOrderIssue) {
      return { target: "position-strike" as const, message: positionStrikeOrderIssue.message };
    }
    const quantity = Number(draft.legs[0]?.contracts);
    if (!Number.isInteger(quantity) || quantity <= 0) {
      return { target: "position-quantity" as const, message: draft.structure === "single"
        ? "Quantity is missing. Enter a positive whole number of contracts."
        : "Quantity is missing. Enter a positive whole number of spreads." };
    }
    if (usesStrategyEntryBasis && !draft.strategyEntryBasisType) {
      return { target: "position-basis-type" as const, message: draft.structure === "single"
        ? "Bought or sold is missing. Choose whether you bought or sold the option."
        : "Opening type is missing. Choose whether you paid a net debit or received a net credit." };
    }
    if (usesStrategyEntryBasis && (!draft.strategyEntryBasisPerShare
      || numberInput(draft.strategyEntryBasisPerShare) === null
      || Number(draft.strategyEntryBasisPerShare) <= 0)) {
      return { target: "position-basis-price" as const, message: draft.structure === "single"
        ? "Opening price is missing. Enter the option's opening price per share."
        : "Opening price is missing. Enter the combined net opening price for all legs." };
    }
    return null;
  };

  const dteConflictMessage = () => {
    if (!positionDteReachability || positionDteReachability.reachable) return "";
    if (positionDteReachability.reason === "ALREADY_PASSED") {
      return `Exactly ${dteThreshold} DTE has already passed for this position. Choose “that many or fewer” or a lower exact day.`;
    }
    const eligibleDays = positionDteReachability.daysUntilEligible;
    return `This DTE alert can first match in ${eligibleDays} ${eligibleDays === 1 ? "day" : "days"}, after the ${duration}-day monitoring window. Choose a longer window or another alert.`;
  };

  const validateCurrentStep = () => {
    if (step === "goal") {
      if (!goal) return "Choose what you want to watch.";
      if (goal === "position" && !positionEnabled) return "Position monitoring is not available for this account.";
      if (goal === "price_target" && !priceTargetEnabled) return "Price-target watches are not available for this account.";
      if (goal === "structure_entry" && !setupEntryEnabled) return "Structure-entry monitoring is not available for this account.";
      return "";
    }
    if (step === "structure") {
      if (isMultiLeg) {
        if (!structureChosen) return isPosition ? "Choose the position you hold." : "Choose the setup you want to watch.";
        const draftFieldError = structureDraftValidation();
        if (draftFieldError) return draftFieldError.message;
        if (!isPosition && searchTickers && confirmedTicker !== draft.underlying.trim().toUpperCase()) return "Ticker is missing. Choose a ticker from the search results.";
        try {
          if (isPosition) {
            buildMultiLegPosition({ ...draft, openedAt: parsePositionOpenedAt(formatPositionOpenedAt(draft.openedAt)) });
          } else {
            buildMultiLegCandidate(draft);
          }
          return "";
        } catch (reason) {
          return reason instanceof Error ? reason.message : "Complete the contract details.";
        }
      }
      if (signalCatalog && !marketSignal && !marketRecipe && !movingAverageDraft && !momentumDraft) {
        if (movingAverageComparisonRequested) return "Choose what to compare with Moving averages.";
        if (momentumIndicatorRequested) return "Choose RSI, MACD, or rate of change.";
        if (volatilityMeasurementRequested) return "Choose a completed-day volatility measurement.";
        return percentageReferenceRequested
          ? "Choose what to compare the percentage move with."
          : "Choose a market signal.";
      }
      if (searchTickers && confirmedTicker !== ticker.trim().toUpperCase()) {
        return "Choose a ticker from the search results.";
      }
      if (marketRecipe && !marketRecipeDefinition) {
        return "This guided recipe is no longer available. Choose another market condition.";
      }
      if ((marketRecipe || isPercentageMove || isMovingAverage || isMomentum || isVolatility || isStandardizedOptionChain) && !confirmedListingIdentity) {
        return "Choose an eligible active U.S. stock or ETF from the search results.";
      }
      return /^[A-Z][A-Z0-9.-]{0,14}$/.test(ticker.trim().toUpperCase()) ? "" : "Enter a valid underlying ticker.";
    }
    if (step === "setup") {
      if (momentumDraft) {
        if (momentumDraft.indicator === "MACD" && !momentumDraft.comparison) {
          return "Choose whether the MACD line should cross its signal line or zero.";
        }
        if (momentumDraft.indicator !== "MACD" && !momentumDraft.triggerMode) {
          return "Choose whether to alert on an existing measurement or only a new crossing.";
        }
        return "";
      }
      if (!movingAverageDraft) return "Choose a market condition.";
      if (!movingAverageDraft.averageType) return "Choose simple or exponential moving average.";
      if (movingAverageDraft.comparison === "PRICE_TO_AVERAGE" && !movingAverageDraft.window) {
        return "Choose the number of completed days in the average.";
      }
      if (movingAverageDraft.comparison === "AVERAGE_CROSSOVER" && !movingAverageDraft.windowPair) {
        return "Choose the faster and slower averages.";
      }
      return "";
    }
    if (step === "trigger") {
      if (isStructureEntry) {
        const supportedTargets = capabilities?.entryTargetTypes?.[draft.structure];
        if (supportedTargets && !supportedTargets.includes(entryTargetType)) {
          return "Entry price type is not available for this strategy. Choose a supported entry price type.";
        }
        if (!/^\d+(?:\.\d{1,4})?$/.test(entryTarget.trim()) || Number(entryTarget) < 0) {
          return "Entry target is missing or invalid. Enter a non-negative USD-per-share price with up to four decimals.";
        }
        try {
          buildOptionConditionSpec();
          return "";
        } catch (reason) {
          return reason instanceof Error ? reason.message : "Complete the contract condition or remove it.";
        }
      }
      if (isPosition) {
        try {
          buildPositionExitEvaluationSpec(draft, {
            logic: positionExitLogic,
            profitTarget,
            lossLimit,
            maxProfitPercent,
            maxLossPercent,
            dteThreshold,
            dteOperator,
            shortStrikeBreach,
            shortStrikeSide,
          }, effectivePositionPayoffPreview.availability);
          buildOptionConditionSpec();
          if (positionDteConflictBlocks) return dteConflictMessage();
          return "";
        } catch (reason) {
          return reason instanceof Error ? reason.message : "Choose at least one valid position alert.";
        }
      }
      if (marketRecipeDefinition) {
        return invalidRecipeParameter()?.message ?? "";
      }
      if (movingAverageDraft) {
        if (movingAverageDraft.comparison === "PRICE_TO_AVERAGE"
          && !movingAverageDraft.triggerMode) {
          return "Choose whether to alert on an existing relationship or only a new crossing.";
        }
        if (!movingAverageDraft.direction) return "Choose above or below.";
        if (signalCatalog) {
          try {
            buildMarketSignalPreviewBody({
              catalog: signalCatalog,
              signal: null,
              movingAverageDraft,
              ticker: underlying,
              listingIdentity: confirmedListingIdentity,
              operator: null,
              threshold: "",
              policy: {
                maxDurationDays: duration,
                evaluationIntervalMinutes: interval,
                maxAlertsPerWatch: alertMode === "one_and_done" ? 1 : 20,
                cooldownMinutes: alertMode === "one_and_done" ? 0 : 120,
                marketHoursOnly,
              },
            });
          } catch (reason) {
            return reason instanceof Error ? reason.message : "Enter a valid Moving averages condition.";
          }
        }
        return "";
      }
      if (momentumDraft) {
        if (!momentumDraft.direction) return "Choose above or below.";
        try {
          buildMarketSignalPreviewBody({
            catalog: signalCatalog,
            signal: null,
            momentumDraft,
            ticker: underlying,
            listingIdentity: confirmedListingIdentity,
            operator: null,
            threshold: "",
            policy: {
              maxDurationDays: duration,
              evaluationIntervalMinutes: interval,
              maxAlertsPerWatch: alertMode === "one_and_done" ? 1 : 20,
              cooldownMinutes: alertMode === "one_and_done" ? 0 : 120,
              marketHoursOnly,
            },
          });
        } catch (reason) {
          return reason instanceof Error ? reason.message : "Enter a valid Momentum condition.";
        }
        return "";
      }
      if (!targetOperator) return isPercentageMove
        ? "Choose Up or Down."
        : isStandardizedOptionChain
          ? "Choose whether the measurement should be at or above or at or below your level."
          : "Choose whether the price should rise above or fall below your level.";
      if (marketSignal && signalCatalog) {
        try {
          buildMarketSignalPreviewBody({
            catalog: signalCatalog,
            signal: marketSignal,
            movingAverageDraft: null,
            ticker: underlying,
            listingIdentity: confirmedListingIdentity,
            operator: targetOperator,
            threshold: targetThreshold,
            policy: {
              maxDurationDays: duration,
              evaluationIntervalMinutes: interval,
              maxAlertsPerWatch: alertMode === "one_and_done" ? 1 : 20,
              cooldownMinutes: alertMode === "one_and_done" ? 0 : 120,
              marketHoursOnly,
            },
          });
          return "";
        } catch (reason) {
          return reason instanceof Error ? reason.message : "Enter a valid market condition.";
        }
      }
      const value = numberInput(targetThreshold);
      return value !== null && value > 0
        ? ""
        : "Enter a target price greater than zero, using up to four decimals.";
    }
    if (step === "schedule") {
      return duration > 0 && interval > 0 ? "" : "Choose a valid schedule.";
    }
    return "";
  };

  /** Optional contract-condition errors must never take focus before the primary alert is complete. */
  const primaryTriggerIsIncomplete = () => {
    if (isStructureEntry) {
      const supportedTargets = capabilities?.entryTargetTypes?.[draft.structure];
      return Boolean(supportedTargets && !supportedTargets.includes(entryTargetType))
        || !/^\d+(?:\.\d{1,4})?$/.test(entryTarget.trim())
        || Number(entryTarget) < 0;
    }
    if (!isPosition) return false;
    try {
      buildPositionExitEvaluationSpec(draft, {
        logic: positionExitLogic,
        profitTarget,
        lossLimit,
        maxProfitPercent,
        maxLossPercent,
        dteThreshold,
        dteOperator,
        shortStrikeBreach,
        shortStrikeSide,
      }, effectivePositionPayoffPreview.availability);
      return positionDteConflictBlocks;
    } catch {
      return true;
    }
  };

  const requestServerPreview = async () => {
    const payload = buildPayload();
    const requestedRevision = form.revision;
    const requestId = previewRequestRef.current + 1;
    previewRequestRef.current = requestId;
    try {
      setPreviewing(true);
      setError("");
      setPreviewRetryAvailable(false);
      const preview = await onPreview(payload);
      if (requestId !== previewRequestRef.current || requestedRevision !== observedRevisionRef.current) {
        return false;
      }
      if (!isAcceptedCreationPreview(preview, goal, payload)) {
        setServerPreview(null);
        setPreviewedPayload(null);
        onDraftChange?.();
        setPreviewRetryAvailable(true);
        setError("We couldn't confirm these settings with the server. Nothing was created. Check the details above, or try the check again.");
        return false;
      }
      setServerPreview(preview);
      setPreviewedPayload(JSON.stringify(payload));
      return true;
    } catch (reason) {
      if (requestId !== previewRequestRef.current || requestedRevision !== observedRevisionRef.current) {
        return false;
      }
      setServerPreview(null);
      setPreviewedPayload(null);
      const code = watchRecipeErrorCode(reason);
      if (marketRecipe && isUnavailableRecipeCode(code)) {
        recipeAuthorityObservedRef.current = false;
        setStep("structure");
        setValidationTarget("market-signal");
        dispatch({
          type: "market_recipe.selected",
          recipe: null,
          parameters: {},
          evaluationIntervalMinutes: 15,
        });
        onDraftChange?.();
        onRetryCapabilities?.();
        setError("That guided recipe is no longer available. Choose another market condition.");
      } else if (marketRecipe && code === "WATCH_RECIPE_SELECTOR_INVALID") {
        setStep("structure");
        setValidationTarget("market-ticker");
        setError("Choose an eligible active U.S. stock or ETF again.");
      } else if (marketRecipe && (code === "WATCH_RECIPE_DRAFT_INVALID"
        || code === "WATCH_RECIPE_DRAFT_REQUIRED")) {
        setStep("trigger");
        setValidationTarget("recipe-parameters");
        setInvalidRecipeParameterId(invalidRecipeParameter()?.parameterId ?? null);
        setError("Review the recipe limits before checking this Watch again.");
      } else {
        setPreviewRetryAvailable(true);
        setError("We couldn't check this Watch right now. Nothing was created. Try the check again in a moment.");
      }
      return false;
    } finally {
      if (requestId === previewRequestRef.current) setPreviewing(false);
    }
  };

  const next = async () => {
    const validationError = validateCurrentStep();
    if (validationError) {
      const target = step === "structure" && isMultiLeg
        ? structureDraftValidation()?.target ?? null
        : step === "structure" && !isMultiLeg
        ? signalCatalog && !marketSignal && !marketRecipe && !movingAverageDraft && !momentumDraft
          ? movingAverageComparisonRequested
            ? "moving-average-comparison"
            : momentumIndicatorRequested
              ? "momentum-indicator"
            : volatilityMeasurementRequested
              ? "volatility-measurement"
            : percentageReferenceRequested
              ? "market-reference"
              : "market-signal"
          : "market-ticker"
        : step === "setup" && movingAverageDraft
          ? !movingAverageDraft.averageType
            ? "moving-average-type"
            : movingAverageDraft.comparison === "PRICE_TO_AVERAGE" && !movingAverageDraft.window
              ? "moving-average-window"
              : "moving-average-pair"
        : step === "setup" && momentumDraft
          ? momentumDraft.indicator === "MACD"
            ? "momentum-comparison"
            : "momentum-mode"
          : step === "trigger" && !isPosition && !isStructureEntry
          ? movingAverageDraft
            ? movingAverageDraft.comparison === "PRICE_TO_AVERAGE"
              && !movingAverageDraft.triggerMode
              ? "moving-average-mode"
              : "moving-average-direction"
            : momentumDraft
              ? !momentumDraft.direction
                ? "momentum-direction"
                : momentumDraft.indicator === "MACD" ? "momentum-direction" : "momentum-level"
            : marketRecipeDefinition
              ? "recipe-parameters"
            : !targetOperator
              ? "market-operator"
              : "market-threshold"
          : step === "trigger" && isStructureEntry && primaryTriggerIsIncomplete()
            ? "entry-target"
          : step === "trigger" && optionEvidenceDraft && !primaryTriggerIsIncomplete()
            ? "option-condition"
          : null;
      setValidationTarget(target);
      setInvalidRecipeParameterId(target === "recipe-parameters"
        ? invalidRecipeParameter()?.parameterId ?? null
        : null);
      setError(validationError);
      return;
    }
    clearError();
    const nextStep = visibleSteps[currentStepIndex + 1];
    if (nextStep?.id === "review" && !(await requestServerPreview())) return;
    if (nextStep) setStep(nextStep.id);
  };

  /** Shared cleanup for ANY backward navigation (Back button, stepper
   * jump-back, review Edit links): step error state, the revealed-condition
   * requests that only apply while authoring the structure step, and the
   * review-bound server preview are all positional to the current step and
   * must not leak onto the step being returned to. */
  const goToEarlierStep = (targetIndex: number) => {
    if (targetIndex < 0 || targetIndex >= currentStepIndex) return;
    setError("");
    setPreviewRetryAvailable(false);
    if (step === "structure") {
      setPercentageReferenceRequested(false);
      setMovingAverageComparisonRequested(false);
      setMomentumIndicatorRequested(false);
      setVolatilityMeasurementRequested(false);
    }
    clearServerPreview();
    const targetStep = visibleSteps[targetIndex];
    if (targetStep) setStep(targetStep.id);
  };

  const back = () => goToEarlierStep(currentStepIndex - 1);

  const editReviewSection = (targetStep: WizardStep) => {
    setError("");
    setPreviewRetryAvailable(false);
    clearServerPreview();
    if (targetStep === "schedule") {
      setScheduleTouched(true);
      setStep("trigger");
      return;
    }
    setStep(targetStep);
  };

  /** Threshold edits keep the direction radio aligned with the live quote until the customer overrides it. */
  const changeTargetThreshold = (threshold: string) => {
    applyTransition({ type: "target_threshold.changed", threshold });
    clearError();
    if (directionSource === "user" || !isPlainPriceCondition) return;
    const spot = marketQuote?.ticker === underlying.trim().toUpperCase() ? marketQuote.price : null;
    const value = numberInput(threshold);
    if (spot === null || value === null || value <= 0) return;
    const operator = value >= spot ? "gte" : "lte";
    if (targetOperator !== operator) applyTransition({ type: "target_operator.changed", operator });
    setDirectionSource("inferred");
  };

  const chooseTargetOperator = (operator: "gte" | "lte") => {
    setDirectionSource("user");
    applyTransition({ type: "target_operator.changed", operator });
    clearError();
  };

  /**
   * Sets the target price from a preset chip and infers direction from
   * whether the preset price sits above or below the live quote, the same
   * rule manual threshold edits use in changeTargetThreshold.
   */
  const chooseTargetPreset = (price: number) => {
    const spot = marketQuote?.ticker === underlying.trim().toUpperCase() ? marketQuote.price : null;
    applyTransition({ type: "target_threshold.changed", threshold: price.toFixed(2) });
    clearError();
    if (spot === null || price <= 0) return;
    const operator = price >= spot ? "gte" : "lte";
    applyTransition({ type: "target_operator.changed", operator });
    setDirectionSource("inferred");
  };

  const buildPayload = () => {
    if (isStructureEntry) {
      const candidate = buildMultiLegCandidate(draft);
      const effectiveDuration = capWatchDurationAtExpiry(duration, [draft.expiry]);
      const optionEvidenceSpec = buildOptionConditionSpec();
      return {
        ticker: underlying.trim().toUpperCase(),
        intent: "structure_entry",
        policy: {
          max_duration_days: effectiveDuration,
          evaluation_interval_minutes: interval,
          max_alerts_per_watch: alertMode === "one_and_done" ? 1 : 20,
          cooldown_minutes: alertMode === "one_and_done" ? 0 : 120,
          market_hours_only: marketHoursOnly,
        },
        evaluation_spec: {
          schema_version: 1,
          subject: "STRUCTURE_ENTRY",
          candidate,
          target: {
            type: entryTargetType,
            threshold_per_share: entryTarget.trim(),
          },
        },
        ...(optionEvidenceSpec ? { option_evidence_spec: optionEvidenceSpec } : {}),
      };
    }
    if (!isPosition && marketRecipeDefinition && marketRecipe) {
      return buildWatchRecipeDraft({
        recipeId: marketRecipe.recipeId,
        definitionVersion: marketRecipe.definitionVersion,
        ticker: underlying,
        listingIdentity: confirmedListingIdentity,
        parameters: marketRecipeParameters,
        policy: {
          maxDurationDays: duration,
          evaluationIntervalMinutes: interval,
          maxAlertsPerWatch: alertMode === "one_and_done" ? 1 : 20,
          cooldownMinutes: alertMode === "one_and_done" ? 0 : 120,
          marketHoursOnly,
        },
      });
    }
    if (!isPosition && (marketSignal || movingAverageDraft || momentumDraft) && signalCatalog) {
      return buildMarketSignalPreviewBody({
        catalog: signalCatalog,
        signal: marketSignal,
        movingAverageDraft,
        momentumDraft,
        ticker: underlying,
        listingIdentity: confirmedListingIdentity,
        operator: targetOperator,
        threshold: targetThreshold,
        policy: {
          maxDurationDays: duration,
          evaluationIntervalMinutes: interval,
          maxAlertsPerWatch: alertMode === "one_and_done" ? 1 : 20,
          cooldownMinutes: alertMode === "one_and_done" ? 0 : 120,
          marketHoursOnly,
        },
      });
    }
    const position = isPosition
      ? buildMultiLegPosition({ ...draft, openedAt: parsePositionOpenedAt(formatPositionOpenedAt(draft.openedAt)) })
      : undefined;
    if (isPosition && position) {
      const optionEvidenceSpec = buildOptionConditionSpec();
      return {
        ticker: underlying.trim().toUpperCase(),
        intent: "position_pnl",
        policy: {
          max_duration_days: capWatchDurationAtExpiry(duration, [draft.expiry]),
          evaluation_interval_minutes: interval,
          max_alerts_per_watch: alertMode === "one_and_done" ? 1 : 20,
          cooldown_minutes: alertMode === "one_and_done" ? 0 : 120,
          market_hours_only: marketHoursOnly,
        },
        position,
        evaluation_spec: buildPositionExitEvaluationSpec(draft, {
          logic: positionExitLogic,
          profitTarget,
          lossLimit,
          maxProfitPercent,
          maxLossPercent,
          dteThreshold,
          dteOperator,
          shortStrikeBreach,
          shortStrikeSide,
        }, effectivePositionPayoffPreview.availability),
        ...(optionEvidenceSpec ? { option_evidence_spec: optionEvidenceSpec } : {}),
        // JS-1081 (adversarial-review FIX 4): reads the LIVE reducer
        // `provenance` state, NOT `initialState?.provenance` directly --
        // the reducer detaches it (sets it to `null`) the moment the
        // customer makes an identity-changing edit (ticker, a leg's
        // strike/option-type/side, expiration, or the overall structure).
        // A trigger-only or basis/quantity edit never touches it, so an
        // otherwise-unmodified pre-filled Watch still submits with its
        // provenance intact. Absent entirely for a manual, un-prefilled
        // Watch (this key is simply absent from the payload then, matching
        // every submission before this field existed) or once detached.
        ...(provenance
          ? { provenance: { kind: provenance.kind, ledger_id: provenance.ledgerId } }
          : {}),
      };
    }
    const state: WatchPayloadState = {
      ticker: underlying.trim().toUpperCase(),
      intent: isPosition ? "position_pnl" : "price_target",
      intentRequiresContract: false,
      contractMode: "builder",
      contractType: "call",
      contractExpiry: "",
      contractStrike: "",
      manualOCC: "",
      duration,
      interval,
      maxAlerts: alertMode === "one_and_done" ? 1 : 20,
      cooldown: alertMode === "one_and_done" ? 0 : 120,
      marketHoursOnly,
      confidenceThreshold: 0.7,
      ivThreshold: 80,
      targetPrice: isPosition ? null : numberInput(targetThreshold),
      targetDirection: targetOperator === "lte" ? "below" : "above",
      volatilitySensitivity: "medium",
      alertMode,
      conditions: [],
      conditionLogic: isPosition ? "OR" : "AND",
      recipeConditions: [],
      position,
      positionProfitTarget: null,
      positionLossLimit: null,
    };
    return buildWatchRequestBody(state);
  };

  const submit = async () => {
    const validationError = validateCurrentStep();
    if (validationError) {
      setError(validationError);
      return;
    }
    const payload = buildPayload();
    if (copyMode && !copyConfirmed) {
      setError(copyMode === "replace"
        ? "Confirm that this replacement will stop the source Watch before continuing."
        : "Confirm that this is a separate Watch before continuing.");
      return;
    }
    if (previewedPayload !== JSON.stringify(payload)
      || !isAcceptedCreationPreview(serverPreview, goal, payload)) {
      setError("Return to the previous step and check this Watch again before creating it.");
      return;
    }
    try {
      setSubmitting(true);
      setError("");
      await onCreate(payload);
      // The routed page owns its success navigation so it can return to the
      // exact newly-created row and expand its detail inline. Embedded dialog
      // callers retain their established close-on-success behavior.
      if (!isPage) onClose();
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : "";
      const code = watchRecipeErrorCode(reason);
      if (marketRecipe && isUnavailableRecipeCode(code)) {
        recipeAuthorityObservedRef.current = false;
        clearServerPreview();
        setStep("structure");
        setValidationTarget("market-signal");
        dispatch({
          type: "market_recipe.selected",
          recipe: null,
          parameters: {},
          evaluationIntervalMinutes: 15,
        });
        onDraftChange?.();
        onRetryCapabilities?.();
        setError("That guided recipe is no longer available. Nothing was created. Choose another market condition.");
      } else if (marketRecipe && (code === "WATCH_RECIPE_DRAFT_INVALID"
        || code === "WATCH_RECIPE_SELECTOR_INVALID")) {
        clearServerPreview();
        setStep(code === "WATCH_RECIPE_SELECTOR_INVALID" ? "structure" : "trigger");
        setValidationTarget(code === "WATCH_RECIPE_SELECTOR_INVALID" ? "market-ticker" : "recipe-parameters");
        setInvalidRecipeParameterId(code === "WATCH_RECIPE_DRAFT_INVALID"
          ? invalidRecipeParameter()?.parameterId ?? null
          : null);
        setError(code === "WATCH_RECIPE_SELECTOR_INVALID"
          ? "Choose an eligible active U.S. stock or ETF again. Nothing was created."
          : "Review the recipe limits. Nothing was created.");
      } else {
        setError(/confirm|preview|expired|revalid|changed|no longer available/i.test(message)
          ? "This Watch check expired or its availability changed. Go back one step, then review it again. Nothing was created."
          : "We couldn't confirm whether this Watch was saved. Try again to check the same request safely.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const positionText = `${readableStructure(draft.structure)} on ${draft.underlying.trim().toUpperCase() || "your underlying"}`;
  const goalDescription = isPosition
    ? "Alert when a price, risk, time, or short-strike condition on a position you hold is met."
    : isStructureEntry
      ? "Alert when an option or supported spread reaches the entry price you choose."
      : isMovingAverage
        ? "Alert from a completed-daily moving-average relationship you choose."
        : isMomentum
          ? "Alert from a completed-daily momentum measurement you choose."
        : "Alert when the selected market signal reaches the condition you choose.";
  const entryTargetText = entryTargetType === "NET_DEBIT_LTE"
    ? `Net debit at or below $${entryTarget || "—"} per share`
    : `Net credit at or above $${entryTarget || "—"} per share`;
  const entryTargetMoneyText = entryTargetMoneyMirror(
    entryTarget,
    draft.structure === "single" ? "contract" : "spread",
    draft.legs[0]?.contracts ?? "",
  );
  const positionAlertSummary = isPosition
    ? describePositionExitDraft(draft, {
      logic: positionExitLogic,
      profitTarget,
      lossLimit,
      maxProfitPercent,
      maxLossPercent,
      dteThreshold,
      dteOperator,
      shortStrikeBreach,
      shortStrikeSide,
    }, effectivePositionPayoffPreview.availability)
    : { items: [], issues: [] };
  const positionAlertItems = positionAlertSummary.items;
  const positionAlertIssues = positionAlertSummary.issues;
  const positionAlertText = positionAlertItems.join(` ${positionExitLogic} `) || "No complete position alert yet";
  const sharedStrikeWarning = isPosition ? sharedShortStrikeAnyWarning(draft, {
    logic: positionExitLogic,
    shortStrikeBreach,
    shortStrikeSide,
  }) : null;
  const zeroMaxProfitWarning = isPosition
    && effectivePositionPayoffPreview.availability?.maxProfitUnavailableReason === "ZERO_MAX_PROFIT"
    ? "This entry basis has no profitable outcome at expiration, so a percentage-of-maximum-profit alert is unavailable. This describes expiration payoff, not interim prices."
    : null;
  const positionPayoffDollarItems = [
    selectedPayoffDollarText(effectivePositionPayoffPreview.availability?.maxProfitUsd, maxProfitPercent, "profit"),
    selectedPayoffDollarText(effectivePositionPayoffPreview.availability?.maxLossUsd, maxLossPercent, "loss"),
  ].filter((item): item is string => Boolean(item));
  const entryBasisText = isPosition && !(draft.structure === "single" && !singleSideChosen)
    ? positionEntryBasisSummary(draft)
    : null;
  const riskDisclosure = isPosition ? shortOptionRisk(draft) : null;
  const payloadDuration = (() => {
    try {
      const payload = buildPayload() as {
        policy?: { max_duration_days?: unknown };
        signal_watch_draft?: { policy?: { max_duration_days?: unknown } };
      };
      const policy = payload.signal_watch_draft?.policy ?? payload.policy;
      const value = Number(policy?.max_duration_days);
      return Number.isFinite(value) && value > 0 ? value : duration;
    } catch {
      return duration;
    }
  })();
  const priceObservation = serverPreview?.observation;
  const recipeServerPreview: WatchRecipeCreationPreview | null = (() => {
    if (serverPreview?.kind !== "RECIPE_WATCH") return null;
    try {
      if (!isMatchingWatchRecipeCreationPreview(serverPreview, buildPayload())) return null;
      return serverPreview;
    } catch {
      return null;
    }
  })();
  const recipePreviewConditions = recipeServerPreview?.conditions as readonly {
    leaf_id: string;
    sentence: string;
  }[] | undefined;
  const StepHeading = isPage ? "h2" : "h3";
  const PreviewHeading = isPage ? "h3" : "h4";
  const ReviewSectionHeading = isPage ? "h3" : "h4";
  const quoteTime = priceObservation?.quoted_at_utc
    ? new Intl.DateTimeFormat("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: "UTC",
    }).format(new Date(priceObservation.quoted_at_utc))
    : null;
  const continueDisabled = previewing
    || (step === "goal" && !goal)
    || (step === "structure" && isMultiLeg && !structureChosen);
  const previewAccepted = (() => {
    if (!serverPreview) return false;
    try {
      return isAcceptedCreationPreview(serverPreview, goal, buildPayload());
    } catch {
      return false;
    }
  })();
  const currentStepLabel = step === "structure"
    ? isPosition
      ? "Position"
      : isStructureEntry
        ? "Entry"
        : isMovingAverage
          ? "Condition"
          : isMomentum
            ? "Condition"
          : "Market"
    : visibleSteps[currentStepIndex]?.label ?? "Watch";
  const normalizedDraftTicker = draft.underlying.trim().toUpperCase();
  const availableStructures = POSITION_STRUCTURES.filter((item) => {
    const advertised = isPosition
      ? capabilities?.positionStructures
      : capabilities?.entryStructures;
    if (advertised) return advertised.includes(item.id);
    return isPosition || item.id !== "single";
  });
  const contractCatalogReady = Boolean(
    marketDataApi
    && normalizedDraftTicker
    && chainLoadedForTicker === normalizedDraftTicker
    && expDates.length > 0,
  );
  const primaryActionLabel = step === "goal"
    ? "Set Up Watch"
    : step === "structure"
      ? isMovingAverage || isMomentum ? "Set Conditions" : "Set Triggers"
      : step === "setup"
        ? "Set Triggers"
      : step === "trigger"
        ? "Review Watch"
        : step === "schedule"
          ? "Review Watch"
          : "Create Watch";
  const scheduleSummary = isCompletedDailySignal
    ? `After each completed market day for up to ${payloadDuration} day${payloadDuration === 1 ? "" : "s"}.`
    : `Every ${interval} minutes for up to ${payloadDuration} day${payloadDuration === 1 ? "" : "s"}${marketHoursOnly ? ", regular hours only" : ""}.`;
  const alertBehaviorSummary = alertMode === "one_and_done"
    ? "Alert once, then complete."
    : isCompletedDailySignal
      ? "Up to 20 alerts, at most once per completed market day."
      : "Up to 20 alerts, no more than once every two hours.";
  const scheduleControls = <>
    <p className={styles.help}>{isCompletedDailySignal
      ? "This Watch checks once after each completed regular-session market day. Choose when it should stop."
      : "Choose how often to check and when this Watch should stop."}</p>
    <div className={styles.fieldGrid}>
      {isCompletedDailySignal
        ? <div className={styles.fixedSchedule}><strong>Check timing</strong><span>After each completed market day</span></div>
        : <label><span>Check every</span><WizardSelect aria-label="Check every" value={String(interval)} onValueChange={(value) => applyTransition({ type: "interval.changed", interval: Number(value) })} options={[{ value: "5", label: "5 minutes" }, { value: "15", label: "15 minutes" }, { value: "30", label: "30 minutes" }, { value: "60", label: "60 minutes" }]} /></label>}
      <label><span>Stop watching after</span><WizardSelect aria-label="Stop watching after" value={String(duration)} onValueChange={(value) => applyTransition({ type: "duration.changed", duration: Number(value) })} options={[{ value: "1", label: "1 day" }, { value: "5", label: "5 days" }, { value: "10", label: "10 days" }, { value: "30", label: "30 days" }]} /></label>
    </div>
    <fieldset className={styles.alertMode}>
      <legend>Alert behavior</legend>
      <label><input type="radio" name="alert-mode" checked={alertMode === "one_and_done"} onChange={() => applyTransition({ type: "alert_mode.changed", alertMode: "one_and_done" })} /><span><strong>Alert once, then complete</strong><small>The Watch stops after its first eligible alert.</small></span></label>
      <label><input type="radio" name="alert-mode" checked={alertMode === "keep_watching"} onChange={() => applyTransition({ type: "alert_mode.changed", alertMode: "keep_watching" })} /><span><strong>Keep checking</strong><small>{isCompletedDailySignal ? "Up to 20 alerts, at most once per completed market day." : "Up to 20 alerts, no more than once every two hours."}</small></span></label>
      {!isCompletedDailySignal && <label className={styles.checkboxLabel} htmlFor={`${headingId}-market-hours`}><span><strong>Regular market hours only</strong><small>Do not evaluate outside regular U.S. market hours.</small></span><Switch id={`${headingId}-market-hours`} checked={marketHoursOnly} onCheckedChange={(checked) => applyTransition({ type: "market_hours.changed", marketHoursOnly: checked })} /></label>}
    </fieldset>
  </>;
  const showSidePanel = isPage && step !== "goal" && step !== "review";
  const sidePanelGoalLabel = isPosition
    ? "Options position"
    : isStructureEntry
      ? "Options entry"
      : "Market";
  const sidePanelRule = isPosition
    ? positionAlertText
    : isStructureEntry
      ? entryTarget.trim()
        ? entryTargetType === "NET_DEBIT_LTE" ? `Debit ≤ $${entryTarget}` : `Credit ≥ $${entryTarget}`
        : "Set your entry price"
      : serverPreview?.sentence ?? "Complete the condition to see it here";
  const requestClose = () => {
    if (form.revision > 0) {
      setDiscardConfirmOpen(true);
      return;
    }
    onClose();
  };

  return (
    <div className={isPage ? styles.page : styles.backdrop} role={isPage ? undefined : "presentation"}>
      <section
        className={isPage ? styles.pageWizard : styles.wizard}
        role={isPage ? undefined : "dialog"}
        aria-modal={isPage ? undefined : true}
        aria-labelledby={headingId}
        onKeyDown={(event) => {
          if (isPage) return;
          if (event.key === "Escape" && !submitting) requestClose();
          if (event.key !== "Tab") return;

          const focusable = Array.from(event.currentTarget.querySelectorAll<HTMLElement>(
            'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
          )).filter((element) => !element.hasAttribute("hidden") && element.getAttribute("aria-hidden") !== "true");
          if (focusable.length === 0) return;

          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }}
      >
        <div className={styles.stickyTop} ref={stickyTopRef}>
        <header className={styles.header}>
          <div>
            {isPage ? <h1 id={headingId}>{copyMode === "replace" ? "Replace this position Watch" : copyMode === "duplicate" ? "Duplicate this position Watch" : "New Watch"}</h1> : <h2 id={headingId}>New Watch</h2>}
            {(copyMode || sourceLabel) && <p className={styles.subtitle}>{copyMode
              ? `Starting from ${hydratedInitialState?.copy?.sourceLabel || "the selected position"}. Review every position fact before creating the new Watch.`
              : sourceLabel
                ? `Starting from ${sourceLabel}. Review every position fact before creating the Watch.`
                : ""}</p>}
          </div>
          {isPage ? (
            <div className={styles.pageHeaderActions}>
              <button ref={closeButtonRef} className={styles.backLink} type="button" onClick={requestClose}>
                <ChevronLeft aria-hidden="true" size={18} />
                Back to Watches
              </button>
            </div>
          ) : (
            <button ref={closeButtonRef} className={styles.closeButton} type="button" onClick={requestClose} aria-label="Close new Watch wizard">
              <X aria-hidden="true" size={20} />
            </button>
          )}
        </header>

        <nav className={styles.progress} aria-label="Watch creation steps">
          <div className={styles.progressMeta}>
            <span>Step {currentStepIndex + 1} of {visibleSteps.length}</span>
            <strong aria-current="step">{currentStepLabel}</strong>
          </div>
          <ol className={styles.progressTrack} style={{ gridTemplateColumns: `repeat(${visibleSteps.length}, 1fr)` }}>
            {visibleSteps.map((item, index) => {
              const stepState = index < currentStepIndex ? "done" : index === currentStepIndex ? "current" : "upcoming";
              const label = <span className={styles.progressStepLabel}>{item.label}</span>;
              const segment = <span className={index <= currentStepIndex ? styles.progressSegmentActive : styles.progressSegment} />;
              const stateClass = stepState === "done"
                ? styles.progressStepDone
                : stepState === "current"
                  ? styles.progressStepCurrent
                  : styles.progressStepUpcoming;
              return (
                <li key={item.id} className={stateClass}>
                  {stepState === "done" ? (
                    <button type="button" className={styles.progressStepButton} aria-label={`Back to ${item.label}`} onClick={() => goToEarlierStep(index)}>
                      {label}
                      {segment}
                    </button>
                  ) : (
                    <span className={styles.progressStepStatic} aria-current={stepState === "current" ? "step" : undefined}>
                      {label}
                      {segment}
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
        </div>
        {/* A card tap can auto-advance the step without a click on a
            differently-labeled control, so screen readers need an explicit
            announcement in addition to the heading-focus move below. */}
        <p className={styles.srOnly} role="status" aria-live="polite">
          {`Step ${currentStepIndex + 1} of ${visibleSteps.length}: ${currentStepLabel}`}
        </p>

        <div className={showSidePanel ? styles.bodyWithSide : styles.body}>
          <div className={styles.main} ref={stepContentRef}>
            {error && <div id={`${headingId}-step-error`} className={styles.error} role="alert" tabIndex={-1} ref={errorRef}><strong>Check this step</strong><span>{error}</span>{previewRetryAvailable && <button type="button" className={styles.errorRetry} onClick={() => void next()} disabled={previewing}>{previewing ? "Checking…" : "Try the check again"}</button>}</div>}

            {step === "goal" && <section aria-labelledby={`${headingId}-goal`}>
              <StepHeading id={`${headingId}-goal`}>What do you want to watch?</StepHeading>
              <p className={styles.help}>Choose the outcome you want to monitor.</p>
              {capabilityState === "loading" && <p className={styles.capabilityNotice} role="status">Checking which Watch types are available…</p>}
              {capabilityState === "error" && <div className={styles.capabilityNotice} role="status">
                <span>We couldn’t load every Watch type. You can still watch a position you hold.</span>
                {onRetryCapabilities && <button type="button" onClick={onRetryCapabilities}>Try again</button>}
              </div>}
              <div className={styles.goalGrid} role="group" aria-label="Watch goal">
                <button className={goal === "position" ? styles.goalCardSelected : styles.goalCard} type="button" onClick={() => chooseGoalAndAdvance("position")} disabled={!positionEnabled} aria-pressed={goal === "position"}>
                  <span className={styles.goalRadio} aria-hidden="true">{goal === "position" && <span />}</span>
                  <span className={styles.goalCopy}><strong>I already hold an options position</strong><span>Monitor profit, loss, time to expiration, or short-strike risk.</span></span>
                </button>
                {setupEntryEnabled ? (
                  <button className={goal === "structure_entry" ? styles.goalCardSelected : styles.goalCard} type="button" onClick={() => chooseGoalAndAdvance("structure_entry")} aria-pressed={goal === "structure_entry"}>
                    <span className={styles.goalRadio} aria-hidden="true">{goal === "structure_entry" && <span />}</span>
                    <span className={styles.goalCopy}><strong>I’m waiting to enter an options trade</strong><span>Watch a call, put, or supported spread for your target price.</span></span>
                  </button>
                ) : (
                  <div className={styles.goalCardDisabled} aria-label="I’m waiting to enter an options trade, not yet available">
                    <span className={styles.goalRadio} aria-hidden="true" />
                    <span className={styles.goalCopy}><strong>I’m waiting to enter an options trade</strong><span>{capabilityState === "loading" ? "Checking availability…" : capabilityState === "error" ? "Availability could not be confirmed. Try again." : "Entry alerts are not available for this account yet."}</span></span>
                  </div>
                )}
                {priceTargetEnabled ? (
                  <button className={goal === "price_target" ? styles.goalCardSelected : styles.goalCard} type="button" onClick={() => chooseGoalAndAdvance("price_target")} aria-pressed={goal === "price_target"}>
                    <span className={styles.goalRadio} aria-hidden="true">{goal === "price_target" && <span />}</span>
                    <span className={styles.goalCopy}><strong>I want to watch the market</strong><span>Track a stock or ETF’s price, volatility, trend, momentum, or option-chain conditions.</span></span>
                  </button>
                ) : (
                  <button className={styles.goalCardDisabled} type="button" disabled>
                    <span className={styles.goalRadio} aria-hidden="true" />
                    <span className={styles.goalCopy}><strong>I want to watch the market</strong><span>{capabilityState === "loading" ? "Checking availability…" : capabilityState === "error" ? "Availability could not be confirmed. Try again." : "Market alerts are not available for this account yet."}</span></span>
                  </button>
                )}
              </div>
            </section>}

            {step === "structure" && isMultiLeg && <section aria-labelledby={`${headingId}-structure`}>
              <StepHeading id={`${headingId}-structure`}>{isPosition ? "Choose the position you own" : "Choose the trade you want to enter"}</StepHeading>
              <p className={styles.help}>Choose a strategy first. We’ll show only the details needed for that choice.</p>
              <fieldset className={styles.structureCards}>
                <legend>Strategy</legend>
                {availableStructures.map((item) => <label key={item.id} className={structureChosen && draft.structure === item.id ? styles.structureCardSelected : styles.structureCard}>
                  <input type="radio" name="structure" checked={structureChosen && draft.structure === item.id} onChange={() => setStructure(item.id)} />
                  <span><strong>{item.label}</strong><small>{structureDescription(item.id)}</small></span>
                  {structureChosen && draft.structure === item.id && <Check aria-hidden="true" size={18} />}
                </label>)}
              </fieldset>
              {structureChosen && <div className={styles.revealedFields}>
                <div className={styles.sectionHeading}>
                  <strong>Contract details</strong>
                  <span>{readableStructure(draft.structure)}</span>
                </div>
                <div className={styles.contractDetailsCard}>
                <div className={styles.fieldGrid}>
                  <TickerLookupField
                    value={draft.underlying}
                    confirmedValue={confirmedTicker}
                    onValueChange={changeOptionsTicker}
                    onSelect={selectOptionsTicker}
                    searchTickers={searchTickers}
                    label="Ticker"
                    ariaLabel="Underlying symbol"
                    invalid={validationTarget === "position-ticker"}
                    errorMessageId={error ? `${headingId}-step-error` : undefined}
                  />
                  {marketDataApi ? <label>
                    <span>Expiration</span>
                    <WizardSelect
                      value={draft.expiry}
                      onValueChange={chooseExpiration}
                      disabled={!contractCatalogReady || loadingExpiration}
                      aria-describedby={`${headingId}-chain-status`}
                      aria-label="Expiration"
                      invalid={validationTarget === "position-expiry"}
                      placeholder={loadingExpiration ? "Loading expirations…" : contractCatalogReady ? "Choose expiration" : "Select a ticker first"}
                      selectedLabel={draft.expiry ? formatExpirationDate(draft.expiry) : undefined}
                      options={expDates.map((expiry) => ({ value: expiry, label: `${formatExpiration(expiry)}${isMonthlyExpiry(expiry) ? " · monthly" : ""}` }))}
                    />
                  </label> : <label><span>Expiration</span><input type="date" value={draft.expiry} aria-label="Expiration" aria-invalid={validationTarget === "position-expiry"} onChange={(event) => updateDraft({ expiry: event.target.value })} /></label>}
                  {isPosition && <label><span>Date opened <small>Optional</small></span><WizardDatePicker aria-label="Date opened" value={openedDateText} placeholder="Today" invalid={validationTarget === "position-opened-date"} onChange={(value) => { setOpenedDateText(value); try { updateDraft({ openedAt: value ? parsePositionOpenedAt(`${value}T00:00`) : Math.floor(Date.now() / 1000) }); } catch (reason) { setError(reason instanceof Error ? reason.message : "Enter a valid open date."); } }} /></label>}
                </div>
                <div className={styles.fieldGridHelperRow}>
                  <span />
                  <span />
                  {isPosition ? <small>Leave blank if you opened it today.</small> : <span />}
                </div>
                {marketDataApi && (loadingExpiration || chainError || chainStatus || currentPrice !== null || draft.expiry) && <div className={chainError ? styles.contractLookupError : styles.contractLookupStatus} id={`${headingId}-chain-status`} role="status" aria-live="polite">
                  <span>{loadingExpiration
                    ? `Loading contracts for ${normalizedDraftTicker || "this ticker"}…`
                    : chainError || chainStatus}</span>
                  {chainError && normalizedDraftTicker && <button type="button" onClick={() => void getExpiryDate(normalizedDraftTicker, true)}>Try again</button>}
                  {currentPrice !== null && <small>{normalizedDraftTicker || "The underlying"} is trading at ${currentPrice.toFixed(2)}.</small>}
                  {!loadingExpiration && !chainError && draft.expiry && <small>Expires {formatExpiration(draft.expiry)}{isMonthlyExpiry(draft.expiry) ? " · monthly listing" : ""}.</small>}
                </div>}
                </div>
                {(draft.structure === "single" || draft.structure === "vertical") && <label className={styles.compactField}><span>{draft.structure === "single" ? "Option type" : "Spread type"}</span><WizardSelect aria-label={draft.structure === "single" ? "Option type" : "Spread type"} value={draft.legs[0]?.optionType || "put"} onValueChange={(value) => setOptionType(value as "call" | "put")} options={[{ value: "put", label: draft.structure === "single" ? "Put" : "Put spread" }, { value: "call", label: draft.structure === "single" ? "Call" : "Call spread" }]} /></label>}
                {draft.structure === "single" && isPosition && <div className={styles.strategyBasis}><fieldset className={styles.structureChoices} aria-invalid={validationTarget === "position-basis-type"}><legend>Did you buy or sell it?</legend><label className={singleSideChosen && draft.legs[0]?.side === "LONG" ? styles.choiceSelected : styles.choice}><input type="radio" name="single-side" checked={singleSideChosen && draft.legs[0]?.side === "LONG"} onChange={() => setSinglePositionSide("LONG")} />Bought</label><label className={singleSideChosen && draft.legs[0]?.side === "SHORT" ? styles.choiceSelected : styles.choice}><input type="radio" name="single-side" checked={singleSideChosen && draft.legs[0]?.side === "SHORT"} onChange={() => setSinglePositionSide("SHORT")} />Sold</label></fieldset></div>}
                <div className={draft.structure === "single" && isStructureEntry ? styles.contractCard : styles.legsHeadingGroup}>
                <div className={styles.legsHeading}>
                  <strong>{draft.structure === "single" ? "Option contract" : "Option legs"}</strong>
                  <span>{draft.structure === "iron_butterfly" ? "Choose the bought wings and one shared strike for the sold put and call." : draft.structure === "single" && isStructureEntry ? "Buy one long option. Short single-option entries are not supported." : "Enter the strike for each leg."}</span>
                </div>

                <div className={styles.legEditor} aria-label={isPosition ? "Position legs" : "Setup legs"}>
                  {(draft.structure === "iron_condor" ? [{ label: "Put spread", indices: [0, 1] }, { label: "Call spread", indices: [2, 3] }] : [{ label: "", indices: draft.structure === "iron_butterfly" ? [0, 1, 3] : draft.legs.map((_, index) => index) }]).map((group) => <div key={group.label || "legs"} className={group.label ? styles.legGroup : undefined} role={group.label ? "group" : undefined} aria-label={group.label || undefined}>
                  {group.label && <div className={styles.legGroupHeading}><strong>{group.label}</strong><span>{group.label === "Put spread" ? "The bought put is the lower outside wing." : "The bought call is the upper outside wing."}</span></div>}
                  {group.indices.map((index) => {
                    const leg = draft.legs[index];
                    const isCenter = draft.structure === "iron_butterfly" && index === 1;
                    const centerMismatch = isCenter && draft.legs[1].strike !== draft.legs[2].strike && (numberInput(draft.legs[1].strike) === null || numberInput(draft.legs[2].strike) === null || Number(draft.legs[1].strike) !== Number(draft.legs[2].strike));
                    const strikeValue = centerMismatch ? "" : leg.strike;
                    const strikeLabel = isCenter ? "Center strike" : `${legRoleLabel(draft.structure, index)} ${leg.optionType} strike`;
                    const strikeInvalid = validationTarget === "position-strike" && (numberInput(strikeValue) === null || Number(strikeValue) <= 0 || positionStrikeOrderIssue?.legIndex === index || isCenter && positionStrikeOrderIssue?.legIndex === 2);
                    const changeStrike = (strike: string) => isCenter ? setButterflyCenterStrike(strike) : updateLeg(index, { strike });
                    const optionStrikes = expiryStrikesByType[leg.optionType]?.[draft.expiry] || [];
                    const availableStrikes = isCenter ? optionStrikes.filter((strike) => (expiryStrikesByType.call?.[draft.expiry] || []).includes(strike)) : optionStrikes;
                    const visibleStrikes = showAllStrikes
                      ? availableStrikes
                      : nearTheMoneyStrikes(availableStrikes, currentPrice, strikeValue);
                    const selectedStrikeMissing = Boolean(strikeValue) && !availableStrikes.includes(strikeValue);
                    const legRowClassName = isPosition
                      ? (draft.structure === "single" ? styles.legRow : styles.legRowSharedQuantity)
                      : (isStructureEntry && draft.structure === "single"
                        ? styles.legRowEntrySingle
                        : styles.candidateLegRowSharedQuantity);
                    return <fieldset className={legRowClassName} key={`${leg.side}-${index}`}>
                    <legend>{isCenter ? "Shared center · short put and call" : legRoleLabel(draft.structure, index)}</legend>
                    <div className={styles.legIdentity}>
                      <span className={draft.structure === "single" && isPosition && !singleSideChosen ? styles.optionType : leg.side === "LONG" ? styles.longBadge : styles.shortBadge}>{draft.structure === "single" && isPosition && !singleSideChosen ? "Side not selected" : leg.side === "LONG" ? (isPosition ? "Bought" : "Buy") : (isPosition ? "Sold" : "Sell")}</span>
                      <span className={styles.optionType}>{isCenter ? "Put + Call" : leg.optionType === "call" ? "Call" : "Put"}</span>
                    </div>
                    {marketDataApi ? <label>
                      <span>{isCenter ? "Center strike" : "Strike"}</span>
                      <WizardSelect
                        value={strikeValue}
                        onValueChange={changeStrike}
                        disabled={!draft.expiry || loadingExpiration || availableStrikes.length === 0}
                        aria-label={strikeLabel}
                        invalid={strikeInvalid}
                        placeholder={!draft.expiry ? "Choose expiration first" : availableStrikes.length === 0 ? (isCenter ? "No shared put/call strikes" : `No ${leg.optionType} strikes found`) : "Choose strike"}
                        options={[
                          ...(selectedStrikeMissing ? [{ value: strikeValue, label: `$${formatStrike(strikeValue)} · current selection` }] : []),
                          ...visibleStrikes.map((strike) => ({ value: strike, label: `$${formatStrike(strike)}${describeStrikeVsPrice(strike, currentPrice)}` })),
                        ]}
                      />
                    </label> : <label><span>{isCenter ? "Center strike" : "Strike"}</span><input inputMode="decimal" value={strikeValue} onChange={(event) => changeStrike(event.target.value)} placeholder="500" aria-label={strikeLabel} aria-invalid={strikeInvalid} /></label>}

                    {centerMismatch && <p className={styles.centerNotice} role="status">The copied put center is ${draft.legs[1].strike || "—"} and call center is ${draft.legs[2].strike || "—"}. Choose one center strike to update both.</p>}
                  </fieldset>;
                  })}</div>)}
                </div>
                {marketDataApi && currentPrice !== null && Boolean(draft.expiry) && draft.legs.some((leg) => (
                  (expiryStrikesByType[leg.optionType]?.[draft.expiry] || []).length > 24
                )) && <button
                  type="button"
                  className={styles.strikeRangeToggle}
                  aria-pressed={showAllStrikes}
                  onClick={() => setShowAllStrikes((current) => !current)}
                >{showAllStrikes ? "Show strikes near the current price" : "Show all strikes"}</button>}
                </div>
                <div className={styles.legsHeading}><strong>{isPosition ? "Position size and opening price" : "Trade size"}</strong><span>{isPosition ? "Enter how much you hold and what you paid or received." : "Choose how many contracts or spreads to monitor."}</span></div>
                {draft.structure !== "single" && <label className={styles.sharedQuantity}><span>Number of spreads</span><input inputMode="numeric" aria-label="Number of spreads" aria-invalid={validationTarget === "position-quantity"} value={draft.legs[0]?.contracts || ""} onChange={(event) => setSharedQuantity(event.target.value)} /><small>The same quantity applies to every leg.</small></label>}
                {draft.structure === "single" && <label className={styles.sharedQuantity}><span>Number of contracts</span><input inputMode="numeric" aria-label="Number of contracts" aria-invalid={validationTarget === "position-quantity"} value={draft.legs[0]?.contracts || ""} onChange={(event) => updateLeg(0, { contracts: event.target.value })} /></label>}
                {usesStrategyEntryBasis && <div className={styles.strategyBasis}>
                  {draft.structure !== "single" ? <fieldset className={styles.structureChoices} aria-invalid={validationTarget === "position-basis-type"}>
                    <legend>How did the position open?</legend>
                    <label className={draft.strategyEntryBasisType === "NET_DEBIT" ? styles.choiceSelected : styles.choice}><input type="radio" name="strategy-entry-basis" checked={draft.strategyEntryBasisType === "NET_DEBIT"} onChange={() => setStrategyEntryBasisType("NET_DEBIT")} />Net debit (paid)</label>
                    <label className={draft.strategyEntryBasisType === "NET_CREDIT" ? styles.choiceSelected : styles.choice}><input type="radio" name="strategy-entry-basis" checked={draft.strategyEntryBasisType === "NET_CREDIT"} onChange={() => setStrategyEntryBasisType("NET_CREDIT")} />Net credit (received)</label>
                  </fieldset> : <p><strong>Opening basis</strong>{singleSideChosen ? (draft.legs[0]?.side === "SHORT" ? "You received" : "You paid") : "Choose Bought or Sold above."}</p>}
                  <label><span>{draft.structure === "single" ? "Your price per share" : "Net opening price"}</span><input inputMode="decimal" value={draft.strategyEntryBasisPerShare || ""} onChange={(event) => updateDraft({ strategyEntryBasisPerShare: event.target.value })} placeholder="e.g. 2.50" aria-label="Strategy entry basis in USD per share" aria-invalid={validationTarget === "position-basis-price"} />
                    {draft.structure === "single"
                      ? <small role="status">{singleOptionOpeningBasisConfirmation(draft, singleSideChosen) ?? "Price per share for this option."}</small>
                      : <><small>Enter the combined fill price for all legs, as quoted by your broker. For example, 2.50 means $250 per spread before fees.</small>{multiLegOpeningBasisConfirmation(draft) && <small role="status">{multiLegOpeningBasisConfirmation(draft)}</small>}</>}
                  </label>
                </div>}
                {isPosition && !usesStrategyEntryBasis && <div className={styles.strategyBasis}>
                  <strong>Opening prices by leg</strong>
                  {draft.legs.map((leg, index) => <label key={`premium-${index}`}><span>{legRoleLabel(draft.structure, index)} · {leg.side === "LONG" ? "Premium paid per share" : "Premium received per share"}</span><input inputMode="decimal" aria-label={draft.structure === "single" ? `Premium ${leg.side === "LONG" ? "paid" : "received"} per share` : `${legRoleLabel(draft.structure, index)} premium ${leg.side === "LONG" ? "paid" : "received"} per share`} value={leg.entryPremiumPerShare} onChange={(event) => updateLeg(index, { entryPremiumPerShare: event.target.value })} placeholder="0.00" /></label>)}
                </div>}
                {!showSidePanel && entryBasisText && <p className={styles.rulePreview}><strong>Opening basis</strong>{entryBasisText}</p>}
                {draft.underlying && draft.expiry && !positionStrikeOrderIssue && draft.legs.every((leg) => Number(leg.strike) > 0) && <div className={styles.positionSummary} aria-label={isPosition ? "Position summary" : "Trade summary"}>
                  <strong>{draft.underlying.toUpperCase()} · {readableStructure(draft.structure)}</strong>
                  <span>{formatExpiration(draft.expiry)}{isMonthlyExpiry(draft.expiry) ? " · monthly" : ""}</span>
                  {draft.legs.map((leg, index) => <span key={`summary-${index}`}>{isPosition && draft.structure === "single" && !singleSideChosen ? "Side not selected" : leg.side === "LONG" ? (isPosition ? "Bought" : "Buy") : (isPosition ? "Sold" : "Sell")} {leg.contracts || "—"} {leg.contracts === "1" ? "contract" : "contracts"} · ${formatStrike(leg.strike)} {leg.optionType}</span>)}
                </div>}
              </div>}
            </section>}

            {step === "structure" && !isMultiLeg && <section aria-labelledby={`${headingId}-market`}>
              <StepHeading id={`${headingId}-market`}>Choose what to track</StepHeading>
              <p className={styles.help}>Choose a ticker, then pick the market condition to watch on it.</p>
              <WatchMarketConditionFields
                mode="subject"
                signalCatalog={signalCatalog}
                selectedSignal={marketSignal}
                recipeCatalog={recipeCatalog}
                selectedRecipe={marketRecipe}
                ticker={ticker}
                confirmedTicker={confirmedTicker}
                errorMessageId={`${headingId}-step-error`}
                invalidSignal={validationTarget === "market-signal"}
                invalidReference={validationTarget === "market-reference"}
                invalidMovingAverageComparison={validationTarget === "moving-average-comparison"}
                invalidMomentumIndicator={validationTarget === "momentum-indicator"}
                invalidVolatilityMeasurement={validationTarget === "volatility-measurement"}
                invalidTicker={validationTarget === "market-ticker"}
                onSelectSignal={selectMarketSignal}
                onSelectRecipe={selectMarketRecipe}
                onPercentageExpandedChange={(expanded) => {
                  setPercentageReferenceRequested(expanded);
                  clearError();
                }}
                movingAverageDraft={movingAverageDraft}
                momentumDraft={momentumDraft}
                onSelectMovingAverageComparison={selectMovingAverageComparison}
                onSelectMomentumIndicator={selectMomentumIndicator}
                onMovingAverageExpandedChange={(expanded) => {
                  setMovingAverageComparisonRequested(expanded);
                  clearError();
                }}
                onMomentumExpandedChange={(expanded) => {
                  setMomentumIndicatorRequested(expanded);
                  clearError();
                }}
                selectedVolatilityMeasurement={isVolatility ? marketSignalPresenter.measurement : null}
                onVolatilityExpandedChange={(expanded) => {
                  setVolatilityMeasurementRequested(expanded);
                  clearError();
                }}
                onTickerChange={changePriceTicker}
                onTickerSelect={selectPriceTicker}
                searchTickers={searchTickers}
                percentageReferenceRef={percentageReferenceRef}
                movingAverageComparisonRef={movingAverageComparisonRef}
                momentumIndicatorRef={momentumIndicatorRef}
                volatilityMeasurementRef={volatilityMeasurementRef}
              />
            </section>}

            {step === "setup" && movingAverageDraft && <section aria-labelledby={`${headingId}-moving-average-setup`}>
              <StepHeading id={`${headingId}-moving-average-setup`}>Choose the moving averages</StepHeading>
              <p className={styles.help}>Choose the calculation and time range. Nothing is selected for you.</p>
              <WatchMovingAverageFields
                mode="setup"
                catalog={signalCatalog}
                draft={movingAverageDraft}
                ticker={underlying}
                errorMessageId={`${headingId}-step-error`}
                invalidAverageType={validationTarget === "moving-average-type"}
                invalidWindow={validationTarget === "moving-average-window"}
                invalidWindowPair={validationTarget === "moving-average-pair"}
                onAverageTypeChange={(averageType) => {
                  applyTransition({ type: "moving_average.type_changed", averageType });
                  clearError();
                }}
                onWindowChange={(window) => {
                  applyTransition({ type: "moving_average.window_changed", window });
                  clearError();
                }}
                onWindowPairChange={(windowPair) => {
                  applyTransition({ type: "moving_average.pair_changed", windowPair });
                  clearError();
                }}
                onTriggerModeChange={() => undefined}
                onDirectionChange={() => undefined}
                averageTypeRef={movingAverageTypeRef}
                windowRef={movingAverageWindowRef}
                windowPairRef={movingAveragePairRef}
              />
            </section>}

            {step === "setup" && momentumDraft && <section aria-labelledby={`${headingId}-momentum-setup`}>
              <StepHeading id={`${headingId}-momentum-setup`}>Choose the momentum condition</StepHeading>
              <p className={styles.help}>Choose the exact measurement behavior. Nothing is selected for you.</p>
              <WatchMomentumFields
                mode="setup"
                catalog={signalCatalog}
                draft={momentumDraft}
                ticker={underlying}
                errorMessageId={`${headingId}-step-error`}
                invalidTriggerMode={validationTarget === "momentum-mode"}
                invalidComparison={validationTarget === "momentum-comparison"}
                onTriggerModeChange={(triggerMode) => {
                  applyTransition({ type: "momentum.trigger_mode_changed", triggerMode });
                  clearError();
                }}
                onComparisonChange={(comparison) => {
                  applyTransition({ type: "momentum.comparison_changed", comparison });
                  clearError();
                }}
                onDirectionChange={() => undefined}
                onLevelChange={() => undefined}
                triggerModeRef={momentumModeRef}
                comparisonRef={momentumComparisonRef}
              />
            </section>}

            {step === "trigger" && <section aria-labelledby={`${headingId}-trigger`}>
              <StepHeading id={`${headingId}-trigger`}>{isPosition ? "Set your position alerts" : isStructureEntry ? "Choose your entry price" : marketRecipeDefinition ? "Review the alert conditions" : isMovingAverage || isMomentum ? "Choose when to alert" : isVolatility ? "Set your volatility level" : isPercentageMove ? "Set the percentage move" : isPlainPriceCondition ? "Set your price level" : "Set your alert level"}</StepHeading>
              {optionConditionNotice && <p className={styles.changeNotice} role="status">{optionConditionNotice}</p>}
              {isPosition ? <>
                <p className={styles.help}>Choose up to five alerts. {positionExitLogic === "AND" ? "This copied Watch fires only when every alert is met." : "The Watch fires when any one is met."} It never closes the trade for you.</p>
                <WatchPositionTriggerFields
                  position={draft}
                  payoffAvailability={effectivePositionPayoffPreview.availability}
                  payoffAvailabilityState={effectivePositionPayoffPreview.state}
                  value={{ logic: positionExitLogic, profitTarget, lossLimit, maxProfitPercent, maxLossPercent, dteThreshold, dteOperator, shortStrikeBreach, shortStrikeSide }}
                  onProfitTargetChange={(value) => { applyTransition({ type: "profit_target.changed", value }); clearError(); }}
                  onLossLimitChange={(value) => { applyTransition({ type: "loss_limit.changed", value }); clearError(); }}
                  onMaxProfitPercentChange={(value) => { applyTransition({ type: "max_profit_percent.changed", value }); clearError(); }}
                  onMaxLossPercentChange={(value) => { applyTransition({ type: "max_loss_percent.changed", value }); clearError(); }}
                  onDteThresholdChange={(value) => { applyTransition({ type: "dte_threshold.changed", value }); clearError(); }}
                  onDteOperatorChange={(operator) => { applyTransition({ type: "dte_operator.changed", operator }); clearError(); }}
                  onShortStrikeBreachChange={(enabled) => { applyTransition({ type: "short_strike_breach.changed", enabled }); clearError(); }}
                  onShortStrikeSideChange={(side) => { applyTransition({ type: "short_strike_side.changed", side }); clearError(); }}
                />
                {positionDteReachability && !positionDteReachability.reachable && <div className={positionDteConflictBlocks ? styles.scheduleConflict : styles.scheduleWarning} role={positionDteConflictBlocks ? "alert" : "status"}>
                  <strong>{positionDteConflictBlocks ? "This alert cannot occur during the current monitoring window" : "One OR condition cannot occur during the current monitoring window"}</strong>
                  <span>{dteConflictMessage()}</span>
                  {!positionDteConflictBlocks && <small>The other OR conditions can still fire this Watch.</small>}
                  {positionDteReachability.suggestedDuration && <button type="button" onClick={() => {
                    applyTransition({ type: "duration.changed", duration: positionDteReachability.suggestedDuration! });
                    setScheduleTouched(true);
                    clearError();
                  }}>Use {positionDteReachability.suggestedDuration} days</button>}
                </div>}
                <p className={styles.rulePreview}><strong>Alert rule</strong>{positionAlertText}</p>
                {positionAlertIssues.length > 0 && <ul className={styles.alertIssues} aria-label="Alerts needing attention">
                  {positionAlertIssues.map((issue) => <li key={issue}>{issue}</li>)}
                </ul>}
                {riskDisclosure && <p className={styles.riskDisclosure}>{riskDisclosure}</p>}
              </> : isStructureEntry ? <>
                <p className={styles.help}>Choose the opening price you want. Estimates use ask prices for options you would buy and bid prices for options you would sell. An alert is not a guaranteed fill.</p>
                <p className={styles.targetHint}>{entryTargetType === "NET_CREDIT_GTE"
                  ? `This ${readableStructure(draft.structure).toLowerCase()} opens for a net credit based on its sides and strikes.`
                  : draft.structure === "single"
                    ? "A long option opens for a debit. We’ll alert when its ask reaches your maximum price."
                    : `This ${readableStructure(draft.structure).toLowerCase()} opens for a net debit based on its sides and strikes.`}</p>
                <div className={styles.triggerRow}>
                  <label><span>{entryTargetType === "NET_DEBIT_LTE" ? "Pay no more than" : "Receive at least"}</span><input value={entryTarget} onChange={(event) => { applyTransition({ type: "entry_target.changed", value: event.target.value }); clearError(); }} inputMode="decimal" placeholder="3.15" aria-label="Entry target in USD per share" aria-invalid={validationTarget === "entry-target"} /><small>{entryTargetMoneyText ?? `Enter a net ${entryTargetType === "NET_DEBIT_LTE" ? "debit" : "credit"} per share. Options are priced per share: $3.15 equals $315 per ${draft.structure === "single" ? "contract" : "spread"}.`}</small></label>
                </div>
                <p className={styles.rulePreview}><strong>Alert rule</strong>{entryTargetType === "NET_DEBIT_LTE" ? `Net debit is $${entryTarget || "—"} per share or less` : `Net credit is $${entryTarget || "—"} per share or more`}</p>
              </> : marketRecipeDefinition ? <div className={styles.recipeSummary}>
                <div>
                  <strong>{marketRecipeDefinition.customer.name}</strong>
                  <p>{marketRecipeDefinition.recipe_id === "market.options_volatility_liquidity_trend"
                    ? "Use this when options should be active and reasonably easy to trade, with price trend confirmation. Implied volatility describes option pricing; it is not a recommendation."
                    : marketRecipeDefinition.customer.description}</p>
                </div>
                {recipeAuthoringParameters.length > 0 ? (
                  <fieldset className={styles.recipeParameterGrid}>
                    <legend>Set your limits</legend>
                    {recipeAuthoringParameters.map((parameter) => {
                      const inputId = `${headingId}-recipe-${parameter.parameter_id}`;
                      const descriptionId = `${inputId}-description`;
                      const isInvalid = invalidRecipeParameterId === parameter.parameter_id;
                      const explanation = parameter.parameter_id === "maximum_relative_spread"
                        ? "A smaller bid-ask spread usually means easier trading. Set the widest spread you will accept."
                        : "Set how high 30-day implied volatility must be. This measures option pricing, not whether the trade is good.";
                      return <label key={parameter.parameter_id} htmlFor={inputId}>
                        <span>{parameter.customer.label}</span>
                        <span className={styles.percentInput}>
                          <input
                            ref={(node) => {
                              if (node) recipeParameterRefs.current.set(parameter.parameter_id, node);
                              else recipeParameterRefs.current.delete(parameter.parameter_id);
                            }}
                            id={inputId}
                            value={canonicalRatioToPercent(marketRecipeParameters[parameter.parameter_id] ?? "")}
                            onChange={(event) => {
                              applyTransition({
                                type: "market_recipe.parameter_changed",
                                parameterId: parameter.parameter_id,
                                value: percentToCanonicalRatio(event.target.value),
                              });
                              clearError();
                            }}
                            inputMode="decimal"
                            aria-label={`${parameter.customer.label} in percent`}
                            aria-invalid={isInvalid || undefined}
                            aria-describedby={descriptionId}
                          />
                          <span aria-hidden="true">%</span>
                        </span>
                        <small id={descriptionId}>{explanation} Enter {canonicalRatioToPercent(parameter.minimum)}%–{canonicalRatioToPercent(parameter.maximum)}%.</small>
                      </label>;
                    })}
                  </fieldset>
                ) : null}
                <strong>All of these must be true</strong>
                <ul aria-label={`${marketRecipeDefinition.customer.name} conditions`}>
                  {recipeConditionSentences.map((condition) => (
                    <li key={condition.leafId}>{condition.sentence}</li>
                  ))}
                </ul>
                <small>{marketRecipeDefinition.evidence_time_policy === "SAME_COMPLETED_REGULAR_SESSION"
                  ? "Conditions are fixed and evaluated together after the same completed regular market session."
                  : "The options conditions must be current and the trend conditions must come from the latest eligible completed market session."}</small>
              </div> : movingAverageDraft ? <WatchMovingAverageFields
                mode="alert"
                catalog={signalCatalog}
                draft={movingAverageDraft}
                ticker={underlying}
                errorMessageId={`${headingId}-step-error`}
                invalidTriggerMode={validationTarget === "moving-average-mode"}
                invalidDirection={validationTarget === "moving-average-direction"}
                onAverageTypeChange={() => undefined}
                onWindowChange={() => undefined}
                onWindowPairChange={() => undefined}
                onTriggerModeChange={(triggerMode) => {
                  applyTransition({ type: "moving_average.trigger_mode_changed", triggerMode });
                  clearError();
                }}
                onDirectionChange={(direction) => {
                  applyTransition({ type: "moving_average.direction_changed", direction });
                  clearError();
                }}
                triggerModeRef={movingAverageModeRef}
                directionRef={movingAverageDirectionRef}
              /> : momentumDraft ? <WatchMomentumFields
                mode="alert"
                catalog={signalCatalog}
                draft={momentumDraft}
                ticker={underlying}
                errorMessageId={`${headingId}-step-error`}
                invalidDirection={validationTarget === "momentum-direction"}
                invalidLevel={validationTarget === "momentum-level"}
                onTriggerModeChange={() => undefined}
                onComparisonChange={() => undefined}
                onDirectionChange={(direction) => {
                  applyTransition({ type: "momentum.direction_changed", direction });
                  clearError();
                }}
                onLevelChange={(value) => {
                  applyTransition({ type: "momentum.level_changed", value });
                  clearError();
                }}
                directionRef={momentumDirectionRef}
                levelRef={momentumLevelRef}
              /> : <WatchMarketConditionFields
                mode="condition"
                signalCatalog={signalCatalog}
                selectedSignal={marketSignal}
                targetOperator={targetOperator}
                targetThreshold={targetThreshold}
                underlying={underlying}
                errorMessageId={`${headingId}-step-error`}
                invalidThreshold={validationTarget === "market-threshold"}
                invalidOperator={validationTarget === "market-operator"}
                onTargetOperatorChange={chooseTargetOperator}
                onTargetThresholdChange={changeTargetThreshold}
                currentPrice={isPlainPriceCondition && marketQuote?.ticker === underlying.trim().toUpperCase() ? marketQuote.price : null}
                previousClose={isPlainPriceCondition && marketQuote?.ticker === underlying.trim().toUpperCase() ? marketQuote.previousClose : null}
                onSelectPricePreset={chooseTargetPreset}
                directionInferred={directionSource === "inferred" && targetOperator !== null}
                thresholdInputRef={targetThresholdRef}
                operatorGroupRef={targetOperatorRef}
              />}
              {optionEvidenceBaseSubject && optionConditionDefinitions.length > 0 ? <>
                <WatchOptionConditionFields
                  baseSubject={optionEvidenceBaseSubject}
                  definitions={optionConditionDefinitions}
                  position={draft}
                  value={optionEvidenceDraft ?? {}}
                  invalid={validationTarget === "option-condition"}
                  errorMessageId={error ? `${headingId}-step-error` : undefined}
                  onChange={(value) => {
                    applyTransition({ type: "option_evidence.set", draft: value });
                    clearError();
                  }}
                />
                {optionEvidenceDraft ? <>
                  <p className={styles.help}>{isPosition
                    ? "Your position alert is evaluated first. The contract condition must also be true."
                    : "The target entry price and contract condition must both be true."}</p>
                  <button
                    type="button"
                    className={styles.secondaryButton}
                    onClick={() => {
                      applyTransition({ type: "option_evidence.remove" });
                      clearError();
                    }}
                  >Remove condition</button>
                </> : null}
              </> : optionEvidenceBaseSubject && optionEvidenceDraft ? (
                <section className={styles.unavailableCondition} role="alert">
                  <strong>Contract condition unavailable</strong>
                  <p>The server no longer authorizes this condition. Remove it to continue, or try again after refreshing Watches.</p>
                  <button
                    type="button"
                    className={styles.secondaryButton}
                    onClick={() => {
                      applyTransition({ type: "option_evidence.remove" });
                      clearError();
                    }}
                  >Remove unavailable condition</button>
                </section>
              ) : null}
              <section className={styles.inlineSchedule} aria-labelledby={`${headingId}-inline-schedule`}>
                <header><strong id={`${headingId}-inline-schedule`}>Monitoring schedule</strong><button type="button" aria-expanded={scheduleTouched} onClick={() => setScheduleTouched((current) => !current)}>{scheduleTouched ? "Hide schedule controls" : "Edit schedule"}</button></header>
                <p>{scheduleSummary}</p>
                {payloadDuration < duration && <small>Monitoring is capped at the option expiration.</small>}
                {isPosition && positionDteReachability?.reachable && positionDteReachability.daysUntilEligible !== null && positionDteReachability.daysUntilEligible > 0 && <small>This DTE alert can first match in about {positionDteReachability.daysUntilEligible} {positionDteReachability.daysUntilEligible === 1 ? "day" : "days"} — inside the monitoring window.</small>}
                <small>{alertBehaviorSummary}</small>
                {scheduleTouched && <div className={styles.inlineScheduleControls}>{scheduleControls}</div>}
              </section>
            </section>}

            {step === "schedule" && <section aria-labelledby={`${headingId}-schedule`}>
              <StepHeading id={`${headingId}-schedule`}>Schedule and delivery</StepHeading>
              {scheduleControls}
            </section>}

            {step === "review" && <section aria-labelledby={`${headingId}-review`}>
              <StepHeading id={`${headingId}-review`}>Review your Watch</StepHeading>
              <p className={styles.help}>Check every detail. Nothing has been created yet.</p>
              <div className={styles.reviewSections}>
                <section className={hasDeliveryChannel(notificationPrefs) ? styles.reviewSection : styles.reviewDeliveryWarning} aria-labelledby={`${headingId}-review-delivery`}>
                  <header><ReviewSectionHeading id={`${headingId}-review-delivery`}>Delivery</ReviewSectionHeading><a href="/portal/notifications" target="_blank" rel="noopener noreferrer">Open Alert Preferences</a></header>
                  <p>{describeNotificationPrefs(notificationPrefs)}</p>
                  <ul className={styles.deliveryChips} aria-label="Delivery channel states">
                    {visibleDeliveryChips(notificationPrefs).map((chip) => {
                      const on = chip.read(notificationPrefs);
                      return (
                        <li key={chip.key} className={on ? styles.deliveryChipOn : styles.deliveryChipOff}>
                          <span>{chip.label}</span>
                          <strong>{on ? "On" : "Off"}</strong>
                        </li>
                      );
                    })}
                  </ul>
                </section>
                <section className={styles.reviewSection} aria-labelledby={`${headingId}-review-goal`}>
                  <header><ReviewSectionHeading id={`${headingId}-review-goal`}>Goal</ReviewSectionHeading>{!hydratedInitialState && <button type="button" onClick={() => editReviewSection("goal")}>Edit Goal</button>}</header>
                  <p>{goalDescription}</p>
                </section>
                <section className={styles.reviewSection} aria-labelledby={`${headingId}-review-instrument`}>
                  <header><ReviewSectionHeading id={`${headingId}-review-instrument`}>{isPosition ? "Position" : isStructureEntry ? "Entry" : "Market"}</ReviewSectionHeading><button type="button" onClick={() => editReviewSection("structure")}>Edit {isPosition ? "Position" : isStructureEntry ? "Entry" : "Market"}</button></header>
                  <dl className={styles.reviewList}>
                    <div><dt>Instrument</dt><dd>{isMultiLeg ? `${positionText} · expires ${draft.expiry ? formatExpiration(draft.expiry) : "—"}` : underlying.trim().toUpperCase()}</dd></div>
                    {isMultiLeg && draft.legs.map((leg, index) => <div key={`${leg.side}-${leg.optionType}-${index}`}><dt>Leg {index + 1}</dt><dd>{isPosition ? friendlyLegSummary(leg, draft.expiry, draft) : `${leg.side === "LONG" ? "Buy" : "Sell"} ${leg.contracts || "—"} · ${draft.expiry ? formatExpiration(draft.expiry) : "expiration not entered"} · $${leg.strike || "—"} ${leg.optionType}`}</dd></div>)}
                    {entryBasisText && <div><dt>Opening basis</dt><dd>{entryBasisText}</dd></div>}
                  </dl>
                </section>
                {movingAverageDraft && <section className={styles.reviewSection} aria-labelledby={`${headingId}-review-setup`}>
                  <header><ReviewSectionHeading id={`${headingId}-review-setup`}>Moving-average setup</ReviewSectionHeading><button type="button" onClick={() => editReviewSection("setup")}>Edit Setup</button></header>
                  <dl className={styles.reviewList}>
                    <div><dt>Calculation</dt><dd>{movingAverageDraft.averageType === "SMA" ? "Simple moving average (SMA)" : "Exponential moving average (EMA)"}</dd></div>
                    <div><dt>Completed days</dt><dd>{movingAverageDraft.comparison === "PRICE_TO_AVERAGE" ? `${movingAverageDraft.window}-day average` : movingAverageDraft.windowPair?.replace("_", "-day and ").concat("-day averages")}</dd></div>
                  </dl>
                </section>}
                {momentumDraft && <section className={styles.reviewSection} aria-labelledby={`${headingId}-review-momentum`}>
                  <header><ReviewSectionHeading id={`${headingId}-review-momentum`}>Momentum setup</ReviewSectionHeading><button type="button" onClick={() => editReviewSection("setup")}>Edit Setup</button></header>
                  <dl className={styles.reviewList}>
                    <div><dt>Measurement</dt><dd>{momentumDraft.indicator === "RSI" ? "14-day RSI" : momentumDraft.indicator === "MACD" ? "MACD line" : "14-day rate of change"}</dd></div>
                    <div><dt>Comparison</dt><dd>{momentumDraft.indicator === "MACD"
                      ? momentumDraft.comparison === "SIGNAL_LINE" ? "Signal line" : "Zero"
                      : momentumDraft.triggerMode === "STATE" ? "Current completed-day measurement" : "New completed-day crossing"}</dd></div>
                  </dl>
                </section>}
                <section className={styles.reviewSection} aria-labelledby={`${headingId}-review-alert`}>
                  <header><ReviewSectionHeading id={`${headingId}-review-alert`}>Alert</ReviewSectionHeading><button type="button" onClick={() => editReviewSection(marketRecipeDefinition ? "structure" : "trigger")}>{marketRecipeDefinition ? "Edit Condition" : "Edit Alert"}</button></header>
                  <p>{isPosition
                    ? positionAlertText
                    : isStructureEntry
                      ? `${entryTargetText}${entryTargetMoneyText ? `. ${entryTargetMoneyText}` : ""}`
                      : marketRecipeDefinition
                        ? marketRecipeDefinition.customer.summary
                      : movingAverageDraft
                        ? describeMovingAverageCondition({ ticker: underlying, ...movingAverageDraft })
                        : momentumDraft
                          ? describeMomentumCondition({ ticker: underlying, ...momentumDraft })
                        : describeWatchSignalCondition(
                          marketSignalPresenter,
                          underlying,
                          targetOperator,
                          targetThreshold,
                        )}</p>
                  {isPosition && positionPayoffDollarItems.length > 0 && <ul className={styles.payoffDollarReview} aria-label="Percentage alert dollar equivalents">
                    {positionPayoffDollarItems.map((item) => <li key={item}>{item}</li>)}
                  </ul>}
                  {isPosition && sharedStrikeWarning && <small className={styles.reviewWarning}>{sharedStrikeWarning}</small>}
                  {isPosition && zeroMaxProfitWarning && <small className={styles.reviewWarning}>{zeroMaxProfitWarning}</small>}
                  {marketRecipeDefinition ? <>
                    <p><strong>All of these must be true</strong></p>
                    <ul aria-label={`${marketRecipeDefinition.customer.name} review conditions`}>
                      {recipeConditionSentences.map((condition) => (
                        <li key={condition.leafId}>{condition.sentence}</li>
                      ))}
                    </ul>
                    <small>{marketRecipeDefinition.evidence_time_policy === "SAME_COMPLETED_REGULAR_SESSION"
                      ? "Evaluated together after the same completed regular market session."
                      : "Requires a current eligible options-chain observation and the latest eligible completed regular market session."}</small>
                  </> : null}
                  {movingAverageDraft?.comparison === "PRICE_TO_AVERAGE" && <small>{movingAverageDraft.triggerMode === "STATE"
                    ? "The first fresh completed-day check may alert if this is already true."
                    : "The first fresh completed-day check establishes a baseline; only a later crossing can alert."}</small>}
                  {movingAverageDraft?.comparison === "AVERAGE_CROSSOVER" && <small>The first fresh completed-day check establishes a baseline; only a later crossover can alert.</small>}
                  {momentumDraft && <small>{momentumDraft.indicator !== "MACD" && momentumDraft.triggerMode === "STATE"
                    ? "The first fresh completed-day check may alert if this is already true."
                    : "The first fresh completed-day check establishes a baseline; only a later strict crossing can alert."}</small>}
                  {isPosition && positionAlertItems.length > 1 && <small>{positionExitLogic === "AND" ? "Every condition must be met (preserved deterministic AND)." : "Any one of these conditions can fire the Watch (deterministic OR)."}</small>}
                  {isPosition && positionDteReachability && !positionDteReachability.reachable && !positionDteConflictBlocks && <small className={styles.reviewWarning}>The DTE condition is outside this monitoring window. Another OR condition can still fire the Watch.</small>}
                  {optionConditionReview && <>
                    <p><strong>Also require:</strong> {optionConditionReview}</p>
                    <small>Both the primary alert and this contract condition must be true using complete, current option data.</small>
                  </>}
                </section>
                <section className={styles.reviewSection} aria-labelledby={`${headingId}-review-schedule`}>
                  <header><ReviewSectionHeading id={`${headingId}-review-schedule`}>Schedule</ReviewSectionHeading><button type="button" onClick={() => editReviewSection("schedule")}>Edit Schedule</button></header>
                  <p>{scheduleSummary}</p>
                  {payloadDuration < duration && <small>Monitoring is capped at the option expiration.</small>}
                  <small>{alertBehaviorSummary}</small>
                </section>
              </div>
              {riskDisclosure && <p className={styles.riskDisclosure}>{riskDisclosure}</p>}
              {copyMode && <label className={styles.copyConfirmation}>
                <input type="checkbox" checked={copyConfirmed} onChange={(event) => { setCopyConfirmed(event.target.checked); clearError(); }} />
                <span>{copyMode === "replace"
                  ? "I understand: creating this replacement permanently stops the source Watch. Its existing history remains available."
                  : "I understand: this creates a separate Watch. The source Watch will continue monitoring and both Watches may evaluate independently."}</span>
              </label>}
              <section className={styles.serverPreview} aria-live="polite" aria-labelledby={`${headingId}-server-preview`}>
                <PreviewHeading id={`${headingId}-server-preview`}>Settings checked</PreviewHeading>
                <p>Nothing is created or sent until you choose {copyMode === "replace" ? "Replace Watch" : copyMode === "duplicate" ? "Duplicate Watch" : "Create Watch"}.</p>
                {recipeServerPreview ? <>
                  <p><strong>{recipeServerPreview.sentence}</strong></p>
                  <p>{recipeServerPreview.behavior}</p>
                  <p><strong>{recipeServerPreview.logic.label}</strong></p>
                  <ul
                    className={styles.serverPreviewConditions}
                    aria-label={`${recipeServerPreview.recipe.name} verified conditions`}
                  >
                    {recipePreviewConditions?.map((condition) => (
                      <li key={condition.leaf_id}>{condition.sentence}</li>
                    ))}
                  </ul>
                  <dl className={styles.serverPreviewFacts}>
                    <div><dt>Evidence timing</dt><dd>{recipeServerPreview.evidence_time.display}</dd></div>
                    <div><dt>Schedule</dt><dd>{recipeServerPreview.schedule}</dd></div>
                  </dl>
                </> : serverPreview?.kind === "SIGNAL_WATCH" ? <>
                  <p><strong>{serverPreview.sentence || "Your market condition is valid."}</strong></p>
                  <p>{isCompletedDailySignal
                    ? "This condition is checked deterministically after each eligible completed market day."
                    : "This condition is checked deterministically after the Watch is created."}</p>
                </> : serverPreview?.kind === "PRICE_TARGET_V1" ? (
                  priceObservation?.state === "AVAILABLE" ? <>
                    <p>Latest available underlying price: <strong>${priceObservation.value_per_share}</strong> per share.</p>
                    <p>{quoteTime ? `Observed ${quoteTime} UTC` : "Observation time unavailable"}{priceObservation.freshness?.age_seconds != null ? ` · ${priceObservation.freshness.age_seconds}s old` : ""}.</p>
                    <p>This price is shown for context only. Your Watch starts checking after it is created.</p>
                  </> : <>
                    <p>{customerPricePreviewReason(priceObservation?.reason_code)}</p>
                    <p>Your settings are valid. The Watch will wait for a current price before evaluating.</p>
                  </>
                ) : <p>The Watch will wait for complete, current quotes before evaluating.</p>}
                <p><strong>Configuration:</strong> Valid. <strong>Delivery:</strong> {hasDeliveryChannel(notificationPrefs)
                  ? "Ready with your configured alert channel."
                  : "No alert channel is configured. You can create the Watch now and add one later in Alert Preferences."}</p>
              </section>
              <div className={styles.disclosure}>
                <span>{isMultiLeg
                  ? "Checks use complete, current quotes · standard 100-share contracts · fees excluded."
                  : isCompletedDailySignal
                    ? "Checks use complete data from finished market days."
                    : "Checks use complete, current prices."}</span>
                <details>
                  <summary>How checks handle missing data</summary>
                  <p>{isMultiLeg
                    ? "If a required quote is missing or out of date, we won’t send an alert from that check. We’ll try again at the next scheduled check."
                    : isCompletedDailySignal
                      ? "If a required completed daily price is missing, incomplete, or out of date, we won’t send an alert from that check. We’ll try again after the next eligible completed market day."
                      : "If a required market price is missing or out of date, we won’t send an alert from that check. We’ll try again at the next scheduled check."}</p>
                </details>
              </div>
            </section>}
          </div>

          {showSidePanel && <aside className={styles.sidePanel} aria-label="Watch summary">
            <h3>Your Watch</h3>
            <dl>
              <div className={styles.sidePanelExtra}><dt>Goal</dt><dd>{sidePanelGoalLabel}</dd></div>
              {underlying.trim() && <div><dt>Symbol</dt><dd>{underlying.trim().toUpperCase()}{isMultiLeg && structureChosen ? ` · ${readableStructure(draft.structure)}` : ""}</dd></div>}
              {sidePrice?.ticker === underlying.trim().toUpperCase() && <div><dt>Price</dt><dd className={styles.sidePanelPrice}>${sidePrice.price.toFixed(2)}{sidePrice.changePercent !== null && <span data-direction={sidePrice.changePercent >= 0 ? "up" : "down"}>{sidePrice.changePercent >= 0 ? "+" : ""}{sidePrice.changePercent.toFixed(2)}%</span>}</dd></div>}
              {draft.expiry && <div><dt>Expires</dt><dd>{formatExpiration(draft.expiry)}{isMonthlyExpiry(draft.expiry) ? " · monthly" : ""}</dd></div>}
              {entryBasisText && <div className={styles.sidePanelExtra}><dt>Opening basis</dt><dd>{entryBasisText}</dd></div>}
              <div className={styles.sidePanelRule}><dt>Alert</dt><dd>{sidePanelRule}</dd></div>
              {(step === "trigger" || step === "schedule") && <>
                <div className={styles.sidePanelExtra}><dt>Schedule</dt><dd>{scheduleSummary}</dd></div>
                <div className={styles.sidePanelExtra}><dt>Behavior</dt><dd>{alertBehaviorSummary}</dd></div>
              </>}
              <div className={styles.sidePanelExtra}><dt>Delivery</dt><dd><span className={styles.sidePanelChips}>{visibleDeliveryChips(notificationPrefs).map((chip) => <span key={chip.key} data-on={chip.read(notificationPrefs)}>{chip.label} · {chip.read(notificationPrefs) ? "On" : "Off"}</span>)}</span></dd></div>
            </dl>
          </aside>}
        </div>

        <footer className={styles.footer}>
          <div className={styles.footerInner}>
            <button className={styles.secondaryButton} type="button" onClick={currentStepIndex === 0 ? requestClose : back}>{currentStepIndex === 0 ? "Cancel" : <><ChevronLeft aria-hidden="true" size={16} />Back</>}</button>
            {step === "review" ? <button className={styles.primaryButton} type="button" onClick={submit} disabled={submitting || !previewAccepted || Boolean(copyMode) && !copyConfirmed}>{submitting ? (copyMode === "replace" ? "Replacing Watch…" : copyMode === "duplicate" ? "Duplicating Watch…" : "Creating Watch…") : copyMode === "replace" ? "Replace Watch" : copyMode === "duplicate" ? "Duplicate Watch" : "Create Watch"}</button> : <button className={styles.primaryButton} type="button" onClick={next} disabled={continueDisabled} aria-label="Continue">{previewing ? "Checking Watch…" : <>{primaryActionLabel}<ChevronRight aria-hidden="true" size={16} /></>}</button>}
          </div>
        </footer>
      </section>
      <Dialog open={discardConfirmOpen} onOpenChange={setDiscardConfirmOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Discard this unfinished Watch?</DialogTitle>
          </DialogHeader>
          <p className={styles.subtitle} style={{ margin: 0 }}>Your changes will be lost.</p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDiscardConfirmOpen(false)}
            >
              Keep editing
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                setDiscardConfirmOpen(false);
                onClose();
              }}
            >
              Discard Watch
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
